import React from 'react';
import { buildLayoutMemberNumberMap, getClassMemberDisplayNumbers } from '../utils/classMemberNumbers';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { ClassRecordMemosEmptyFrame, ClassRecordMemosEmptyTable } from '../components/common/ActualEmptyDataFrames';
import ModalDocumentActionButtons from '../components/common/ModalDocumentActionButtons';
import { useProgramSidebar } from '../contexts/ProgramSidebarContext';
import { useCalendarLinkedAcademicYear } from '../hooks/useCalendarLinkedAcademicYear';
import { useProgramProgressData } from '../hooks/useProgramProgressData';
import { useProgramProgressCells } from '../hooks/useProgramProgressCells';
import { useAnnualProjectProgramsResolved } from '../hooks/useAnnualProjectProgramsResolved';
import { getClassMemberGradeLayoutsMap, getClassMembers } from '../services/classMembersService';
import { loadClassMemberNotesForGrade } from '../services/classMemberOwnedDataService';
import { getAnnualBehaviorNotes } from '../services/annualBehaviorNotesService';
import { getProgramFixedContentRows } from '../services/programFixedContentService';
import { getProgramScopedContentRowsForGradeTerm } from '../services/programScopedContentService';
import type {
  AnnualBehaviorNote,
  Grade,
  ProgramFixedContentRow,
  ProgramProgressCell,
  ProgramScopedContentRow,
} from '../types/domain';
import type { MemberNoteRecord } from '../types/pointItemNote';
import { parseGradeDisplayName } from '../utils/gradeDisplayName';
import {
  CLASS_MEMBERS_CHANGED_EVENT,
  emptyGradeLayout,
  loadClassMembers,
  loadClassMemberGradeLayoutFromStorage,
  type ClassMemberGradeLayout,
  type ClassMemberRow,
} from '../utils/classMembersStorage';
import { displayClassMemberName } from '../utils/classMemberNameDisplay';
import {
  extractMemberMatchedTaggedLines,
  isSealedTagBody,
  memoMatchesMember,
  parseClassRecordMemoTags,
  renderNoteWithMemberTags,
  resolveMemberTagUnique,
  type ClassRecordMemoTagRuleOptions,
} from '../utils/classRecordMemoHashtags';
import { useSealedMemoTagLabels } from '../hooks/useSealedMemoTagLabels';
import { peekSealedTagLabel } from '../utils/memoTagLabelCache';
import { buildBehaviorNoteMemoRows } from '../utils/classRecordMemosBehaviorNotes';
import {
  buildPointNoteMemoRows,
  pointNoteRecordKindLabel,
  type PointNoteMemoRecordKind,
} from '../utils/classRecordMemosPointNotes';
import {
  ASSIGNMENT_MEMO_RECORD_KIND,
  buildAssignmentMemoRows,
  resolveLinkedAssignmentMemoNote,
  type AssignmentMemoRecordKind,
} from '../utils/classRecordMemosAssignmentNotes';
import { getAssignmentPointsNote } from '../services/assignmentPointsNoteService';
import type { AssignmentPointsNoteDoc } from '../types/assignmentPointsNote';
import { membersForRecordGroup, recordMatchesClassRecordGroupFilter } from '../utils/classMemberRecordGroupUtils';
import {
  composeClassRecordNoteWithLinkedAssignmentMemo,
  parseMemoFieldStorageDeep,
} from '../utils/programCellDisplayText';
import {
  ANNUAL_BEHAVIOR_NOTES_SAVED_EVENT,
  type AnnualBehaviorNotesSavedDetail,
} from '../utils/annualBehaviorNotesSavedEvents';
import {
  PROGRAM_PROGRESS_SAVED_EVENT,
  type ProgramProgressSavedDetail,
} from '../utils/programProgressSavedEvents';
import { loadProgramProgressCellsCache } from '../utils/programProgressCellsCache';
import { setProgramProgressCellsQueryData } from '../query/cacheUpdates';
import type { AnnualProjectProgram } from '../utils/annualProjectProgramsStorage';
import { formatLearningTextForDisplay, formatUnitTextForDisplay } from '../utils/programCellDisplayText';
import {
  ANNUAL_WEEKDAY_KEYS,
  buildAnnualPeriodLabels,
  dateIsoForMondayWeekday,
  parseAnnualCellKey,
} from '../utils/annualTimetable';
import { parseISODate } from '../utils/date';
import {
  buildProgramProgressRows,
  buildTrackNameByTimetableTextByGrade,
  buildWeeksFromRange,
  resolveClassEntryByGradeId,
  type ProgramProgressRow,
} from '../utils/programProgressRows';
import { CLASS_RECORD_MEMOS_CAPTURE_CSS } from '../utils/classRecordMemosCaptureCss';
import {
  computeScopedOccurrenceForAnnualSlot,
  scopedScheduleSlotRefFromOccurrenceKey,
} from '../utils/annualProgressSlotSource';
import { projectScopedRowsOntoScheduleSlots, type ScopedScheduleSlotRef } from '../utils/scheduleContentMatcher';
import { loadTrackProgramModeMap } from '../utils/trackProgramModeStorage';
import { loadTrackProgramSyncRepMap } from '../utils/trackProgramSyncRepStorage';
import {
  saveClassRecordMemosAsExcel,
  type ClassRecordMemosExportRow,
} from '../utils/classRecordMemosExcelExport';
import {
  printModalBlockContentA4,
  saveModalBlockContentAsPdf,
} from '../utils/modalDocumentExport';

interface ClassRecordMemosPageProps {
  workspaceId: string;
  grades: Grade[];
  currentGradeId?: string;
}

const ALL_MEMBERS_FILTER_ID = '__all_members__';
const ALL_RECORD_GROUPS_FILTER_ID = '__all_record_groups__';
const UNTAGGED_MEMBER_FILTER_ID = '__untagged_member__';

/** 태그별 조회 사이드바/내보내기용 표시 라벨 (`#e:…` → 평문) */
function labelForGeneralQueryTag(tag: string, sealedMap?: Record<string, string>): string {
  const kw = (tag ?? '').trim();
  if (!kw) return '';
  if (isSealedTagBody(kw)) {
    const raw = `#${kw}`;
    return sealedMap?.[raw] ?? peekSealedTagLabel(raw) ?? '비공개 태그';
  }
  return kw;
}
const GENERAL_TAG_OPTION_PREFIX = '__general_tag__::';
const MOBILE_MEMBER_OPTION_PREFIX = '__member__::';

/** `App.css` `.cp-class-record-memos-table tbody tr` 대략 1줄 높이 — 빈 행 채움 계산용 */
const CLASS_RECORD_MEMOS_TABLE_BODY_ROW_PX = 36;
const CLASS_RECORD_MEMOS_TABLE_FILLER_ROWS_CAP = 500;

type CrmColKey =
  | 'term'
  | 'date'
  | 'weekday'
  | 'period'
  | 'program'
  | 'unit'
  | 'learning'
  | 'kind'
  | 'note';

const CRM_COL_KEYS: readonly CrmColKey[] = [
  'term',
  'date',
  'weekday',
  'period',
  'program',
  'unit',
  'learning',
  'kind',
  'note',
];

const CRM_COL_WIDTHS_STORAGE_KEY = 'cp-class-record-memos:colWidthsPx:v1';

/** 기본 열 너비(px). 학기 좁게·교시 넓게(방과후 등). */
const CRM_DEFAULT_COL_WIDTHS_PX: Record<CrmColKey, number> = {
  term: 36,
  date: 72,
  weekday: 44,
  period: 92,
  program: 108,
  unit: 118,
  learning: 204,
  kind: 92,
  note: 280,
};

const CRM_COL_RESIZE_MIN_PX: Record<CrmColKey, number> = {
  term: 28,
  date: 52,
  weekday: 32,
  period: 56,
  program: 64,
  unit: 64,
  learning: 96,
  kind: 64,
  note: 120,
};

const CRM_COL_RESIZE_MAX_PX = 640;

const CRM_COL_LABELS: Record<CrmColKey, string> = {
  term: '학기',
  date: '날짜',
  weekday: '요일',
  period: '교시',
  program: '프로그램',
  unit: '단원',
  learning: '학습내용',
  kind: '구분',
  note: '메모',
};

function clampCrmColWidthPx(key: CrmColKey, value: unknown): number | null {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return null;
  return Math.min(CRM_COL_RESIZE_MAX_PX, Math.max(CRM_COL_RESIZE_MIN_PX[key], n));
}

function loadStoredCrmColWidthsPx(): Record<CrmColKey, number> {
  try {
    const raw = localStorage.getItem(CRM_COL_WIDTHS_STORAGE_KEY);
    if (!raw) return { ...CRM_DEFAULT_COL_WIDTHS_PX };
    const parsed = JSON.parse(raw) as Partial<Record<CrmColKey, number>>;
    const out = { ...CRM_DEFAULT_COL_WIDTHS_PX };
    for (const k of CRM_COL_KEYS) {
      const c = clampCrmColWidthPx(k, parsed[k]);
      if (c != null) out[k] = c;
    }
    return out;
  } catch {
    return { ...CRM_DEFAULT_COL_WIDTHS_PX };
  }
}

/** 멤버 이름 미입력 시 표시 (기록 모아보기 멤버별 조회) */
const MEMBER_NAME_FALLBACK = '이름 없음';

function countClassRecordMemosTableFillerRows(
  wrap: HTMLDivElement,
  table: HTMLTableElement,
  options: { dataRowCount: number; emptyMessageRow: boolean },
): number {
  const thead = table.querySelector('thead');
  const theadH = thead ? Math.ceil(thead.getBoundingClientRect().height) : 0;
  const avail = Math.max(0, wrap.clientHeight - theadH - 2);
  const rowsNeeded = Math.ceil(avail / CLASS_RECORD_MEMOS_TABLE_BODY_ROW_PX);
  const contentRows = options.emptyMessageRow ? 1 : options.dataRowCount;
  return Math.min(
    CLASS_RECORD_MEMOS_TABLE_FILLER_ROWS_CAP,
    Math.max(0, rowsNeeded - contentRows),
  );
}

type MemberSidebarSection = { groupId: string; groupName: string; members: ClassMemberRow[] };

function memberDisplayName(m: ClassMemberRow): string {
  const t = displayClassMemberName(m).trim();
  return t ? t : MEMBER_NAME_FALLBACK;
}

/** 구분 필터: 라벨이 비었거나 '—'인 행 */
const KIND_FILTER_EMPTY_KEY = '__kind_empty__';

const ALL_KIND_FILTER_KEYS: readonly string[] = [
  KIND_FILTER_EMPTY_KEY,
  'lesson',
  'eval_log',
  'behavior',
  'point_general',
  'point_learning',
  'point_behavior',
  ASSIGNMENT_MEMO_RECORD_KIND,
];

type MemosCategory = 'all' | 'general' | 'lesson' | 'behavior';

const MEMOS_CATEGORY_OPTIONS: readonly { id: MemosCategory; label: string }[] = [
  { id: 'all', label: '종합기록' },
  { id: 'general', label: '일반기록' },
  { id: 'lesson', label: '수업(학습)-평가-과제 기록' },
  { id: 'behavior', label: '행동기록' },
];

function kindFilterBucketForRow(row: MemoRow): string {
  const label = (row.kindLabel ?? '').trim();
  if (!label || label === '—') return KIND_FILTER_EMPTY_KEY;
  return row.recordKind;
}

/** 헤더: 표가 날짜·교시 순으로 정렬됨을 표시 */
function ClassRecordMemosSortHintIcon() {
  return (
    <svg
      className="cp-class-record-memos-th-sort-icon"
      width="11"
      height="12"
      viewBox="0 0 11 12"
      aria-hidden
      focusable="false"
    >
      <path fill="currentColor" d="M5.5 1.5 8.5 5.5H2.5L5.5 1.5z" />
      <path fill="currentColor" d="M5.5 10.5 2.5 6.5h6L5.5 10.5z" />
    </svg>
  );
}

type MemoRow = {
  key: string;
  termNumber: 1 | 2;
  /** 학기·날짜(ISO)·교시 순 정렬용 */
  sortKey: string;
  /** 현재 연간 진도표에 해당 칸이 있으면 true (없으면 회색 행) */
  hasProgramSchedule: boolean;
  dateLabel: string;
  weekdayLabel: string;
  periodLabel: string;
  /** 고정→행사, 프로젝트→이름, 편제→편제명 등 */
  programLabel: string;
  /** 프로그램 열 뱃지 종류: 고정·프로젝트·편제(및 전담·일반) / 없음 */
  programDotKind: 'fixed' | 'project' | 'track' | 'none';
  timetableText: string;
  unitLabel: string;
  learningLabel: string;
  recordKind: 'lesson' | 'eval_log' | 'behavior' | PointNoteMemoRecordKind | AssignmentMemoRecordKind;
  kindLabel: string;
  note: string;
  /** 저장된 수업기록 멤버 그룹(null·빈 값은 레거시) */
  recordMemberGroupId: string | null;
  /** 포인트 코인 아이템 멤버 기록일 때 대상 멤버 */
  sourceMemberId?: string;
  /** 수업·평가기록에 연결된 과제 item id */
  linkedAssignmentItemId?: string;
  /** 연결 시 선택한 멤버 그룹(학급) */
  linkedAssignmentGroupId?: string;
  /** 과제메모 행의 원본 과제 item id */
  assignmentItemId?: string;
};

function rowMatchesMemosCategory(row: MemoRow, category: MemosCategory): boolean {
  if (category === 'all') return true;
  if (category === 'general') return row.recordKind === 'point_general';
  if (category === 'lesson') {
    return (
      row.recordKind === 'lesson' ||
      row.recordKind === 'point_learning' ||
      row.recordKind === ASSIGNMENT_MEMO_RECORD_KIND
    );
  }
  if (category === 'behavior') return row.recordKind === 'behavior' || row.recordKind === 'point_behavior';
  return true;
}

/** 프로그램 열 필터: MemoRow.programDotKind 와 동일 키 (모달 목록 순서) */
const ALL_PROGRAM_FILTER_KEYS: readonly MemoRow['programDotKind'][] = ['none', 'fixed', 'project', 'track'];

function programFilterLabel(key: MemoRow['programDotKind']): string {
  if (key === 'none') return '(비어 있음)';
  if (key === 'fixed') return '행사';
  if (key === 'project') return '프로젝트';
  return '편제';
}

function classRecordKindLabel(text: string): string {
  const t = (text ?? '').trim();
  if (t === 'lesson') return '수업기록';
  if (t === 'eval_log') return '평가기록';
  if (t === 'behavior') return '행동특성';
  if (t === ASSIGNMENT_MEMO_RECORD_KIND) return '과제메모';
  const pointLabel = pointNoteRecordKindLabel(t);
  if (pointLabel) return pointLabel;
  return t || '—';
}

function memoTableShortDateLabel(iso: string): string {
  const d = parseISODate(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}월${d.getDate()}일`;
}

/** 편제: scope_key `편제명|시간표명` → 시간표명만(예: 국어|국어 → 국어). 프로젝트: 시간표 미입력과 동일하게 빈 칸 */
function timetableLabelForScopedMemosRow(row: ProgramScopedContentRow): string {
  if (row.scope_type === 'project') return '';
  if (row.scope_type !== 'track') return '';
  const key = (row.scope_key ?? '').trim();
  if (!key) return '';
  const parts = key.split('|').map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 2) return parts[parts.length - 1] ?? '';
  return parts[0] ?? '';
}

type ProgramCellOverlays = {
  recordClassText: Record<string, string>;
  recordMemberGroupId: Record<string, string | null>;
  unitText: Record<string, string>;
  learningText: Record<string, string>;
};

const EMPTY_PROGRAM_CELL_OVERLAYS: ProgramCellOverlays = {
  recordClassText: {},
  recordMemberGroupId: {},
  unitText: {},
  learningText: {},
};

/** 고정 행은 source_annual_cell_key, 편제·프로젝트 행은 일정 순서로 짝지은 칸 키(화면용 사본)로 메인 셀과 합친다 */
function buildProgramCellOverlays(
  fixedRows: ProgramFixedContentRow[],
  scopedRows: ProgramScopedContentRow[]
): ProgramCellOverlays {
  const recordClassText: Record<string, string> = {};
  const recordMemberGroupId: Record<string, string | null> = {};
  const unitText: Record<string, string> = {};
  const learningText: Record<string, string> = {};
  const assign = (out: Record<string, string>, key: string, raw: string) => {
    if (!(raw ?? '').trim()) return;
    out[key] = raw;
  };
  for (const row of fixedRows) {
    const k = (row.source_annual_cell_key ?? '').trim();
    if (!k) continue;
    assign(recordClassText, k, row.record_class_text ?? '');
    assign(unitText, k, row.unit_text ?? '');
    assign(learningText, k, row.learning_text ?? '');
  }
  for (const row of scopedRows) {
    const k = (row.source_annual_cell_key ?? '').trim();
    if (!k) continue;
    assign(recordClassText, k, row.record_class_text ?? '');
    recordMemberGroupId[k] = (row.record_member_group_id ?? '').trim() || null;
    assign(unitText, k, row.unit_text ?? '');
    assign(learningText, k, row.learning_text ?? '');
  }
  return { recordClassText, recordMemberGroupId, unitText, learningText };
}

type ProgramMemosColumnMeta = { label: string; dotKind: 'fixed' | 'project' | 'track' };

/** 연간 셀 키 → 모아보기「프로그램」열 (고정 먼저, 스코프가 같은 키를 덮어씀 — 오버레이와 동일) */
function buildProgramColumnMetaByAnnualKey(
  fixedRows: ProgramFixedContentRow[],
  scopedRows: ProgramScopedContentRow[],
  projectPrograms: AnnualProjectProgram[]
): Record<string, ProgramMemosColumnMeta> {
  const out: Record<string, ProgramMemosColumnMeta> = {};
  for (const row of fixedRows) {
    const k = (row.source_annual_cell_key ?? '').trim();
    if (!k) continue;
    out[k] = { label: '행사', dotKind: 'fixed' };
  }
  for (const row of scopedRows) {
    const k = (row.source_annual_cell_key ?? '').trim();
    if (!k) continue;
    if (row.scope_type === 'track') {
      const sk = (row.scope_key ?? '').trim();
      const head = sk.split('|')[0]?.trim() ?? '';
      out[k] = { label: head || sk || '—', dotKind: 'track' };
    } else if (row.scope_type === 'project') {
      const id = (row.scope_key ?? '').trim();
      const hit = projectPrograms.find((p) => p.id === id);
      out[k] = { label: (hit?.name ?? id).trim() || '프로젝트', dotKind: 'project' };
    }
  }
  return out;
}

function programNameFromScheduleRowColor(
  row: ProgramProgressRow,
  termNumber: 1 | 2,
  programs: AnnualProjectProgram[]
): string | null {
  const c = (row.timetableColorHex ?? '').trim().toLowerCase();
  if (!c) return null;
  const hit = programs.find(
    (p) => p.termNumber === termNumber && p.colorHex.trim().toLowerCase() === c
  );
  return hit?.name ?? null;
}

function programLabelForScopedOrphanRow(
  row: ProgramScopedContentRow,
  projectPrograms: AnnualProjectProgram[]
): string {
  if (row.scope_type === 'track') {
    const sk = (row.scope_key ?? '').trim();
    const head = sk.split('|')[0]?.trim() ?? '';
    return head || sk || '—';
  }
  if (row.scope_type === 'project') {
    const id = (row.scope_key ?? '').trim();
    const hit = projectPrograms.find((p) => p.id === id);
    return (hit?.name ?? id).trim() || '프로젝트';
  }
  return '—';
}

function programDotKindForScopedOrphanRow(row: ProgramScopedContentRow): 'project' | 'track' {
  return row.scope_type === 'project' ? 'project' : 'track';
}

function sanitizeClassRecordMemosExportFileToken(value: string): string {
  return value.trim().replace(/[\\/:*?"<>|]/g, '_').replace(/\s+/g, '_').slice(0, 48);
}

const ClassRecordMemosPage: React.FC<ClassRecordMemosPageProps> = ({ workspaceId, grades, currentGradeId }) => {
  const [searchParams] = useSearchParams();
  const {
    programFocusGradeId,
    setProgramFocusGradeId,
  } = useProgramSidebar();

  const gradeIdFromUrl = searchParams.get('gradeId');
  const effectiveGradeId = gradeIdFromUrl || programFocusGradeId || currentGradeId || '';
  const academicYear = useCalendarLinkedAcademicYear(workspaceId, effectiveGradeId.trim());

  const gradeRow = React.useMemo(
    () => (effectiveGradeId ? grades.find((g) => g.id === effectiveGradeId) ?? null : null),
    [grades, effectiveGradeId]
  );
  const classParsed = React.useMemo(
    () => (gradeRow ? parseGradeDisplayName(gradeRow.name) : null),
    [gradeRow]
  );

  React.useEffect(() => {
    if (effectiveGradeId) setProgramFocusGradeId(effectiveGradeId);
  }, [effectiveGradeId, setProgramFocusGradeId]);

  const prog1 = useProgramProgressData({
    workspaceId,
    grades,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber: 1,
  });
  const prog2 = useProgramProgressData({
    workspaceId,
    grades,
    gradeId: effectiveGradeId,
    academicYear,
    termNumber: 2,
  });

  const cellsT1 = useProgramProgressCells({
    workspaceId,
    academicYear,
    termNumber: 1,
    gradeId: effectiveGradeId,
  });
  const cellsT2 = useProgramProgressCells({
    workspaceId,
    academicYear,
    termNumber: 2,
    gradeId: effectiveGradeId,
  });

  /** 오버레이에 안 실리는 행(source_annual_cell_key 없음 등)도 모아보기에서 직접 읽기 */
  const [fixedScopedRowsForMemos, setFixedScopedRowsForMemos] = React.useState<{
    f1: ProgramFixedContentRow[];
    f2: ProgramFixedContentRow[];
    s1: ProgramScopedContentRow[];
    s2: ProgramScopedContentRow[];
  }>({ f1: [], f2: [], s1: [], s2: [] });
  const [overlayLoading, setOverlayLoading] = React.useState(false);
  const [overlayError, setOverlayError] = React.useState<string | null>(null);
  const [memosCategory, setMemosCategory] = React.useState<MemosCategory>('all');
  const [behaviorNotesT1, setBehaviorNotesT1] = React.useState<AnnualBehaviorNote[]>([]);
  const [behaviorNotesT2, setBehaviorNotesT2] = React.useState<AnnualBehaviorNote[]>([]);

  const reloadMemosOverlaysFromCache = React.useCallback(async () => {
    if (!workspaceId || !effectiveGradeId) {
      setFixedScopedRowsForMemos({ f1: [], f2: [], s1: [], s2: [] });
      return;
    }
    const [f1, f2, s1, s2] = await Promise.all([
      getProgramFixedContentRows(workspaceId, academicYear, 1, effectiveGradeId),
      getProgramFixedContentRows(workspaceId, academicYear, 2, effectiveGradeId),
      getProgramScopedContentRowsForGradeTerm(workspaceId, academicYear, 1, effectiveGradeId),
      getProgramScopedContentRowsForGradeTerm(workspaceId, academicYear, 2, effectiveGradeId),
    ]);
    setFixedScopedRowsForMemos({ f1, f2, s1, s2 });
  }, [academicYear, effectiveGradeId, workspaceId]);

  const reloadBehaviorNotesFromCache = React.useCallback(async () => {
    if (!workspaceId || !effectiveGradeId) {
      setBehaviorNotesT1([]);
      setBehaviorNotesT2([]);
      return;
    }
    const [n1, n2] = await Promise.all([
      getAnnualBehaviorNotes(workspaceId, academicYear, 1, effectiveGradeId),
      getAnnualBehaviorNotes(workspaceId, academicYear, 2, effectiveGradeId),
    ]);
    setBehaviorNotesT1(n1);
    setBehaviorNotesT2(n2);
  }, [academicYear, effectiveGradeId, workspaceId]);

  const syncProgramCellsQueryFromLocalCache = React.useCallback(() => {
    if (!workspaceId || !effectiveGradeId) return;
    for (const termNumber of [1, 2] as const) {
      const disk = loadProgramProgressCellsCache(workspaceId, academicYear, termNumber, effectiveGradeId);
      if (disk.found) {
        setProgramProgressCellsQueryData(workspaceId, academicYear, termNumber, effectiveGradeId, disk.rows);
      }
    }
  }, [academicYear, effectiveGradeId, workspaceId]);

  React.useEffect(() => {
    let cancelled = false;
    if (!workspaceId || !effectiveGradeId) {
      setFixedScopedRowsForMemos({ f1: [], f2: [], s1: [], s2: [] });
      setOverlayError(null);
      setOverlayLoading(false);
      return;
    }
    setOverlayLoading(true);
    setOverlayError(null);
    void (async () => {
      try {
        await reloadMemosOverlaysFromCache();
      } catch (e) {
        if (!cancelled) {
          setOverlayError(e instanceof Error ? e.message : String(e));
          setFixedScopedRowsForMemos({ f1: [], f2: [], s1: [], s2: [] });
        }
      } finally {
        if (!cancelled) setOverlayLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [effectiveGradeId, reloadMemosOverlaysFromCache, workspaceId]);

  React.useEffect(() => {
    let cancelled = false;
    void reloadBehaviorNotesFromCache().catch(() => {
      if (!cancelled) {
        setBehaviorNotesT1([]);
        setBehaviorNotesT2([]);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [reloadBehaviorNotesFromCache]);

  /** 프로그램/연간 저장 후 LS·RQ 캐시만 다시 읽어 반영 (forceRefresh 없음) */
  React.useEffect(() => {
    if (!workspaceId || !effectiveGradeId) return undefined;
    const onProgramSaved = (e: Event) => {
      const d = (e as CustomEvent<ProgramProgressSavedDetail>).detail;
      if (!d?.workspaceId || !d.gradeId) return;
      if (d.workspaceId !== workspaceId || d.gradeId !== effectiveGradeId) return;
      if (d.academicYear !== academicYear) return;
      syncProgramCellsQueryFromLocalCache();
      void reloadMemosOverlaysFromCache().catch(() => undefined);
    };
    const onBehaviorSaved = (e: Event) => {
      const d = (e as CustomEvent<AnnualBehaviorNotesSavedDetail>).detail;
      if (!d?.workspaceId || !d.gradeId) return;
      if (d.workspaceId !== workspaceId || d.gradeId !== effectiveGradeId) return;
      if (d.academicYear !== academicYear) return;
      void reloadBehaviorNotesFromCache().catch(() => undefined);
    };
    const onVisible = () => {
      if (document.visibilityState !== 'visible') return;
      syncProgramCellsQueryFromLocalCache();
      void reloadMemosOverlaysFromCache().catch(() => undefined);
      void reloadBehaviorNotesFromCache().catch(() => undefined);
    };
    window.addEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
    window.addEventListener(ANNUAL_BEHAVIOR_NOTES_SAVED_EVENT, onBehaviorSaved as EventListener);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(PROGRAM_PROGRESS_SAVED_EVENT, onProgramSaved as EventListener);
      window.removeEventListener(ANNUAL_BEHAVIOR_NOTES_SAVED_EVENT, onBehaviorSaved as EventListener);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [
    academicYear,
    effectiveGradeId,
    reloadBehaviorNotesFromCache,
    reloadMemosOverlaysFromCache,
    syncProgramCellsQueryFromLocalCache,
    workspaceId,
  ]);

  const [members, setMembers] = React.useState<ClassMemberRow[]>([]);
  const [memberGradeLayout, setMemberGradeLayout] = React.useState<ClassMemberGradeLayout>(() => emptyGradeLayout());
  const [selectedMemberId, setSelectedMemberId] = React.useState<string | null>(ALL_MEMBERS_FILTER_ID);
  const [selectedGeneralQueryTag, setSelectedGeneralQueryTag] = React.useState<string | null>(null);
  const [generalQueryExpanded, setGeneralQueryExpanded] = React.useState(true);
  const [selectedRecordGroupFilterId, setSelectedRecordGroupFilterId] =
    React.useState<string>(ALL_RECORD_GROUPS_FILTER_ID);

  React.useEffect(() => {
    if (!workspaceId || !effectiveGradeId) {
      setMemberGradeLayout(emptyGradeLayout());
      setMembers([]);
      return;
    }
    setMembers(loadClassMembers(workspaceId, effectiveGradeId));
    setMemberGradeLayout(loadClassMemberGradeLayoutFromStorage(workspaceId, effectiveGradeId) ?? emptyGradeLayout());

    const pullLayoutsFromServer = () => {
      void getClassMemberGradeLayoutsMap(workspaceId, [effectiveGradeId])
        .then((m) => {
          const lay = m[effectiveGradeId];
          if (lay) setMemberGradeLayout(lay);
        })
        .catch(() => undefined);
    };
    pullLayoutsFromServer();

    void getClassMembers(workspaceId, effectiveGradeId)
      .then((rows) => setMembers(rows))
      .catch(() => undefined);

    const onMembersChanged = () => {
      setMembers(loadClassMembers(workspaceId, effectiveGradeId));
      setMemberGradeLayout(loadClassMemberGradeLayoutFromStorage(workspaceId, effectiveGradeId) ?? emptyGradeLayout());
      pullLayoutsFromServer();
    };
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onMembersChanged as EventListener);
    return () => window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onMembersChanged as EventListener);
  }, [workspaceId, effectiveGradeId]);

  const [pointMemberNotes, setPointMemberNotes] = React.useState<MemberNoteRecord[]>([]);
  const [assignmentPointsDoc, setAssignmentPointsDoc] =
    React.useState<AssignmentPointsNoteDoc | null>(null);

  React.useEffect(() => {
    let cancelled = false;
    if (!workspaceId || !effectiveGradeId) {
      setPointMemberNotes([]);
      return;
    }
    const reload = () => {
      void loadClassMemberNotesForGrade(workspaceId, effectiveGradeId)
        .then((rows) => {
          if (!cancelled) setPointMemberNotes(rows);
        })
        .catch(() => {
          if (!cancelled) setPointMemberNotes([]);
        });
    };
    reload();
    const onMembersChanged = () => reload();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    window.addEventListener(CLASS_MEMBERS_CHANGED_EVENT, onMembersChanged as EventListener);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      window.removeEventListener(CLASS_MEMBERS_CHANGED_EVENT, onMembersChanged as EventListener);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [workspaceId, effectiveGradeId]);

  React.useEffect(() => {
    let cancelled = false;
    if (!workspaceId || !effectiveGradeId) {
      setAssignmentPointsDoc(null);
      return;
    }
    const reload = () => {
      void getAssignmentPointsNote(workspaceId, effectiveGradeId)
        .then((doc) => {
          if (!cancelled) setAssignmentPointsDoc(doc);
        })
        .catch(() => {
          if (!cancelled) setAssignmentPointsDoc(null);
        });
    };
    reload();
    const onVisible = () => {
      if (document.visibilityState === 'visible') reload();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [workspaceId, effectiveGradeId]);

  const selectedMember = React.useMemo(
    () => members.find((m) => m.id === selectedMemberId) ?? null,
    [members, selectedMemberId]
  );
  const isAllMembersSelected = selectedMemberId === ALL_MEMBERS_FILTER_ID;
  const isUntaggedMemberSelected = selectedMemberId === UNTAGGED_MEMBER_FILTER_ID;

  const selectedEntry = React.useMemo(
    () => resolveClassEntryByGradeId(prog1.schedule, grades, effectiveGradeId),
    [effectiveGradeId, grades, prog1.schedule]
  );

  const termDef1 = React.useMemo(
    () => prog1.terms.find((t) => t.term_number === 1) ?? null,
    [prog1.terms]
  );
  const termDef2 = React.useMemo(
    () => prog2.terms.find((t) => t.term_number === 2) ?? null,
    [prog2.terms]
  );

  const weeks1 = React.useMemo(
    () => buildWeeksFromRange(termDef1?.starts_on ?? '', termDef1?.ends_on ?? ''),
    [termDef1?.ends_on, termDef1?.starts_on]
  );
  const weeks2 = React.useMemo(
    () => buildWeeksFromRange(termDef2?.starts_on ?? '', termDef2?.ends_on ?? ''),
    [termDef2?.ends_on, termDef2?.starts_on]
  );

  const trackNameByTimetableTextByGrade = React.useMemo(
    () => buildTrackNameByTimetableTextByGrade(prog1.tracks),
    [prog1.tracks]
  );

  const annualProjectPrograms = useAnnualProjectProgramsResolved(workspaceId, effectiveGradeId, academicYear);

  const gradeRawName = React.useMemo(
    () => grades.find((g) => g.id === selectedEntry?.gradeId)?.name ?? selectedEntry?.label ?? '',
    [grades, selectedEntry?.gradeId, selectedEntry?.label]
  );

  const rowsTerm1 = React.useMemo((): ProgramProgressRow[] => {
    if (!selectedEntry || !termDef1) return [];
    return buildProgramProgressRows({
      workspaceId,
      academicYear,
      termNumber: 1,
      selectedEntry,
      gradeRawName,
      termDef: termDef1,
      weeks: weeks1,
      holidaySet: prog1.holidaySet,
      cellsByKey: prog1.cellsByKey,
      dedicatedColors: prog1.dedicatedColors,
      baseExcludeColors: prog1.baseExcludeColors,
      regularColors: prog1.regularColors,
      annualProjectPrograms,
      trackNameByTimetableTextByGrade,
    });
  }, [
    academicYear,
    annualProjectPrograms,
    gradeRawName,
    prog1.cellsByKey,
    prog1.dedicatedColors,
    prog1.baseExcludeColors,
    prog1.holidaySet,
    prog1.regularColors,
    workspaceId,
    selectedEntry,
    termDef1,
    trackNameByTimetableTextByGrade,
    weeks1,
  ]);

  const rowsTerm2 = React.useMemo((): ProgramProgressRow[] => {
    if (!selectedEntry || !termDef2) return [];
    return buildProgramProgressRows({
      workspaceId,
      academicYear,
      termNumber: 2,
      selectedEntry,
      gradeRawName,
      termDef: termDef2,
      weeks: weeks2,
      holidaySet: prog2.holidaySet,
      cellsByKey: prog2.cellsByKey,
      dedicatedColors: prog2.dedicatedColors,
      baseExcludeColors: prog2.baseExcludeColors,
      regularColors: prog2.regularColors,
      annualProjectPrograms,
      trackNameByTimetableTextByGrade,
    });
  }, [
    academicYear,
    annualProjectPrograms,
    gradeRawName,
    prog2.cellsByKey,
    prog2.dedicatedColors,
    prog2.baseExcludeColors,
    prog2.holidaySet,
    prog2.regularColors,
    workspaceId,
    selectedEntry,
    termDef2,
    trackNameByTimetableTextByGrade,
    weeks2,
  ]);

  const trackProgramModeByTrack = React.useMemo(
    () => (workspaceId && effectiveGradeId ? loadTrackProgramModeMap(workspaceId, effectiveGradeId) : {}),
    [effectiveGradeId, workspaceId]
  );
  const trackProgramSyncRepByTrack = React.useMemo(
    () => (workspaceId && effectiveGradeId ? loadTrackProgramSyncRepMap(workspaceId, effectiveGradeId) : {}),
    [effectiveGradeId, workspaceId]
  );
  /** 일정 칸(날짜·교시 순) → 편제/프로젝트 스코프. 공통 매처로 학습내용 행을 순서대로 짝짓는다 */
  const buildScopedSlotsForTerm = React.useCallback(
    (rows: readonly ProgramProgressRow[], termNumber: 1 | 2): ScopedScheduleSlotRef[] => {
      const out: ScopedScheduleSlotRef[] = [];
      rows.forEach((row) => {
        if (row.isScheduleSlotOverflow) return;
        const programKindLabel = row.programKindLabel ?? '';
        if (programKindLabel === '고정') return;
        const color = (row.timetableColorHex ?? '').trim().toLowerCase();
        const project =
          programKindLabel === '프로' && color
            ? annualProjectPrograms.find(
                (p) => p.termNumber === termNumber && p.colorHex.trim().toLowerCase() === color
              ) ?? null
            : null;
        const { scopedOccurrenceKey } = computeScopedOccurrenceForAnnualSlot({
          isFixedSlot: false,
          programKindLabel,
          projectId: project?.id ?? null,
          templateLabel: row.templateLabel ?? '',
          scopeLookupAlias: row.timetableText ?? '',
          trackProgramModeByTrack,
          trackProgramSyncRepByTrack,
        });
        const ref = scopedScheduleSlotRefFromOccurrenceKey(row.annualCellKey, scopedOccurrenceKey);
        if (ref) out.push(ref);
      });
      return out;
    },
    [annualProjectPrograms, trackProgramModeByTrack, trackProgramSyncRepByTrack]
  );
  /** 편제·프로젝트 행의 화면용 칸 키 = 지금 일정에서 짝지어진 칸(넘친 행은 null → 일정 없는 기록으로 표시) */
  const projectedScopedRowsForMemos = React.useMemo(
    () => ({
      s1: projectScopedRowsOntoScheduleSlots(buildScopedSlotsForTerm(rowsTerm1, 1), fixedScopedRowsForMemos.s1),
      s2: projectScopedRowsOntoScheduleSlots(buildScopedSlotsForTerm(rowsTerm2, 2), fixedScopedRowsForMemos.s2),
    }),
    [buildScopedSlotsForTerm, fixedScopedRowsForMemos.s1, fixedScopedRowsForMemos.s2, rowsTerm1, rowsTerm2]
  );
  const cellOverlays1 = React.useMemo(
    () =>
      workspaceId && effectiveGradeId
        ? buildProgramCellOverlays(fixedScopedRowsForMemos.f1, projectedScopedRowsForMemos.s1)
        : EMPTY_PROGRAM_CELL_OVERLAYS,
    [effectiveGradeId, fixedScopedRowsForMemos.f1, projectedScopedRowsForMemos.s1, workspaceId]
  );
  const cellOverlays2 = React.useMemo(
    () =>
      workspaceId && effectiveGradeId
        ? buildProgramCellOverlays(fixedScopedRowsForMemos.f2, projectedScopedRowsForMemos.s2)
        : EMPTY_PROGRAM_CELL_OVERLAYS,
    [effectiveGradeId, fixedScopedRowsForMemos.f2, projectedScopedRowsForMemos.s2, workspaceId]
  );

  const memoRowsBase = React.useMemo((): MemoRow[] => {
    const out: MemoRow[] = [];
    const scheduleByKey1 = new Map(rowsTerm1.map((r) => [r.annualCellKey, r] as const));
    const scheduleByKey2 = new Map(rowsTerm2.map((r) => [r.annualCellKey, r] as const));
    const periodLabels = selectedEntry ? buildAnnualPeriodLabels(selectedEntry.sectionRows) : [];

    const memberGroupById = new Map<string, string>();
    const assignmentMembersById = new Map<
      string,
      { id: string; name: string; orderIndex: number; groupId: string }
    >();
    for (const g of memberGradeLayout.groups) {
      const list = memberGradeLayout.membersByGroupId[g.id] ?? [];
      const nums = getClassMemberDisplayNumbers(list);
      list.forEach((m, index) => {
        memberGroupById.set(m.id, g.id);
        assignmentMembersById.set(m.id, {
          id: m.id,
          name: displayClassMemberName(m).trim(),
          // 0-based 필드: 고정 번호 - 1 (번호가 없으면 순서)
          orderIndex: (nums[index] ?? index + 1) - 1,
          groupId: g.id,
        });
      });
    }

    let linkedAllowedMemberIds: Set<string> | null = null;
    if (selectedRecordGroupFilterId !== ALL_RECORD_GROUPS_FILTER_ID) {
      linkedAllowedMemberIds = new Set(
        (memberGradeLayout.membersByGroupId[selectedRecordGroupFilterId] ?? []).map((m) => m.id)
      );
    }

    const resolveClassRecordDisplay = (raw: string) => {
      const deep = parseMemoFieldStorageDeep(raw);
      const text = deep.text.trim();
      const linkedAssignmentItemId = (deep.assignmentMemoItemId ?? '').trim();
      const linkGroupId = (deep.assignmentMemoGroupId ?? '').trim();
      let allowedForLink: Set<string> | null = linkGroupId
        ? new Set((memberGradeLayout.membersByGroupId[linkGroupId] ?? []).map((m) => m.id))
        : null;
      if (linkedAllowedMemberIds) {
        const pageAllowed = linkedAllowedMemberIds;
        if (!allowedForLink) {
          allowedForLink = pageAllowed;
        } else {
          allowedForLink = new Set(
            Array.from(allowedForLink).filter((id) => pageAllowed.has(id))
          );
        }
      }
      const linkedNote = linkedAssignmentItemId
        ? resolveLinkedAssignmentMemoNote({
            doc: assignmentPointsDoc,
            itemId: linkedAssignmentItemId,
            membersById: assignmentMembersById,
            allowedMemberIds: allowedForLink,
          })
        : '';
      const note = composeClassRecordNoteWithLinkedAssignmentMemo(deep.note, linkedNote);
      return {
        text,
        note,
        linkedAssignmentItemId,
        linkedAssignmentGroupId: linkGroupId || undefined,
      };
    };

    const programMetaByKey1 = buildProgramColumnMetaByAnnualKey(
      fixedScopedRowsForMemos.f1,
      projectedScopedRowsForMemos.s1,
      annualProjectPrograms
    );
    const programMetaByKey2 = buildProgramColumnMetaByAnnualKey(
      fixedScopedRowsForMemos.f2,
      projectedScopedRowsForMemos.s2,
      annualProjectPrograms
    );
    const resolveProgramColumnMeta = (
      annualKey: string,
      termNumber: 1 | 2,
      scheduleRow: ProgramProgressRow | undefined
    ): { label: string; dotKind: 'fixed' | 'project' | 'track' | 'none' } => {
      const metaMap = termNumber === 1 ? programMetaByKey1 : programMetaByKey2;
      const fromMap = metaMap[annualKey];
      if (fromMap && (fromMap.label ?? '').trim()) return fromMap;
      if (scheduleRow) {
        const pk = scheduleRow.programKindLabel;
        if (pk === '고정') return { label: '행사', dotKind: 'fixed' };
        if (pk === '프로') {
          return {
            label:
              programNameFromScheduleRowColor(scheduleRow, termNumber, annualProjectPrograms) ?? '프로젝트',
            dotKind: 'project',
          };
        }
        if (pk === '전담') return { label: '전담', dotKind: 'track' };
        return { label: (classParsed?.title ?? '').trim() || '—', dotKind: 'track' };
      }
      return { label: '—', dotKind: 'none' };
    };

    /** 프로그램 테이블과 동일: 저장 셀 + 오버레이 키를 모두 순회, 연간 진도 행과 매칭해 일정 표시 */
    const pushMemoForAnnualKey = (
      annualKey: string,
      termNumber: 1 | 2,
      cellMap: Record<string, ProgramProgressCell>,
      overlays: ProgramCellOverlays
    ) => {
      const scheduleMap = termNumber === 1 ? scheduleByKey1 : scheduleByKey2;
      const scheduleRow = scheduleMap.get(annualKey);
      const cell = cellMap[annualKey];
      const recordGidFull = (
        overlays.recordMemberGroupId[annualKey] ?? cell?.record_member_group_id ?? ''
      ).trim();
      const raw = overlays.recordClassText[annualKey] ?? cell?.record_class_text ?? '';
      const { text, note, linkedAssignmentItemId, linkedAssignmentGroupId } =
        resolveClassRecordDisplay(raw);
      if (
        !recordMatchesClassRecordGroupFilter({
          selectedFilterId: selectedRecordGroupFilterId,
          allFilterId: ALL_RECORD_GROUPS_FILTER_ID,
          storedGroupId: recordGidFull,
          note,
          layout: memberGradeLayout,
        })
      ) {
        return;
      }
      const recordMemberGroupId = recordGidFull || null;
      if (!note.trim()) return;
      if (text !== 'lesson' && text !== 'eval_log') return;

      const unitRaw = overlays.unitText[annualKey] ?? cell?.unit_text ?? '';
      const learningRaw = overlays.learningText[annualKey] ?? cell?.learning_text ?? '';

      if (scheduleRow) {
        const pm = resolveProgramColumnMeta(annualKey, termNumber, scheduleRow);
        const dateIso = dateIsoForMondayWeekday(scheduleRow.mondayIso, scheduleRow.weekdayKey);
        out.push({
          key: `${termNumber}:${annualKey}`,
          termNumber,
          hasProgramSchedule: true,
          sortKey: `${termNumber}|${dateIso}|${String(scheduleRow.slotIndex).padStart(3, '0')}`,
          dateLabel: scheduleRow.dateLabel,
          weekdayLabel: scheduleRow.weekdayLabel,
          periodLabel: scheduleRow.periodLabel,
          programLabel: pm.label,
          programDotKind: pm.dotKind,
          timetableText: scheduleRow.timetableText,
          unitLabel: formatUnitTextForDisplay(unitRaw),
          learningLabel: formatLearningTextForDisplay(learningRaw),
          recordKind: text,
          kindLabel: classRecordKindLabel(text),
          note,
          recordMemberGroupId,
          ...(linkedAssignmentItemId ? { linkedAssignmentItemId } : {}),
          ...(linkedAssignmentGroupId ? { linkedAssignmentGroupId } : {}),
        });
        return;
      }

      const coords = cell
        ? { mondayIso: cell.monday_iso, weekdayKey: cell.weekday_key, slotIndex: cell.slot_index }
        : parseAnnualCellKey(annualKey);
      if (!coords) {
        const pm = resolveProgramColumnMeta(annualKey, termNumber, undefined);
        out.push({
          key: `${termNumber}:unkeyed:${annualKey}`,
          termNumber,
          hasProgramSchedule: false,
          sortKey: `${termNumber}|\ufffe|${annualKey}`,
          dateLabel: '—',
          weekdayLabel: '—',
          periodLabel: '—',
          programLabel: pm.label,
          programDotKind: pm.dotKind,
          timetableText: (cell?.timetable_text ?? '').trim(),
          unitLabel: formatUnitTextForDisplay(unitRaw),
          learningLabel: formatLearningTextForDisplay(learningRaw),
          recordKind: text,
          kindLabel: classRecordKindLabel(text),
          note,
          recordMemberGroupId,
          ...(linkedAssignmentItemId ? { linkedAssignmentItemId } : {}),
          ...(linkedAssignmentGroupId ? { linkedAssignmentGroupId } : {}),
        });
        return;
      }

      const dateIso = dateIsoForMondayWeekday(coords.mondayIso, coords.weekdayKey);
      const weekdayLabel =
        ANNUAL_WEEKDAY_KEYS.find((w) => w.key === coords.weekdayKey)?.label ?? coords.weekdayKey;
      const periodLabel = periodLabels[coords.slotIndex] || `${coords.slotIndex + 1}타임`;
      const pm = resolveProgramColumnMeta(annualKey, termNumber, undefined);

      out.push({
        key: `${termNumber}:orphan-slot:${annualKey}`,
        termNumber,
        hasProgramSchedule: false,
        sortKey: `${termNumber}|${dateIso}|${String(coords.slotIndex).padStart(3, '0')}`,
        dateLabel: memoTableShortDateLabel(dateIso),
        weekdayLabel,
        periodLabel,
        programLabel: pm.label,
        programDotKind: pm.dotKind,
        timetableText: (cell?.timetable_text ?? '').trim(),
        unitLabel: formatUnitTextForDisplay(unitRaw),
        learningLabel: formatLearningTextForDisplay(learningRaw),
        recordKind: text,
        kindLabel: classRecordKindLabel(text),
        note,
        recordMemberGroupId,
        ...(linkedAssignmentItemId ? { linkedAssignmentItemId } : {}),
        ...(linkedAssignmentGroupId ? { linkedAssignmentGroupId } : {}),
      });
    };

    const keys1 = new Set<string>([
      ...Object.keys(cellsT1.cellsByAnnualKey),
      ...Object.keys(cellOverlays1.recordClassText),
    ]);
    keys1.forEach((k) => pushMemoForAnnualKey(k, 1, cellsT1.cellsByAnnualKey, cellOverlays1));
    const keys2 = new Set<string>([
      ...Object.keys(cellsT2.cellsByAnnualKey),
      ...Object.keys(cellOverlays2.recordClassText),
    ]);
    keys2.forEach((k) => pushMemoForAnnualKey(k, 2, cellsT2.cellsByAnnualKey, cellOverlays2));

    const appendFixedRowsWithoutSourceKey = (rows: readonly ProgramFixedContentRow[], termNumber: 1 | 2) => {
      for (const row of rows) {
        if ((row.source_annual_cell_key ?? '').trim()) continue;
        const raw = row.record_class_text ?? '';
        const { text, note, linkedAssignmentItemId, linkedAssignmentGroupId } =
        resolveClassRecordDisplay(raw);
        if (!note.trim()) continue;
        if (text !== 'lesson' && text !== 'eval_log') continue;
        if (
          !recordMatchesClassRecordGroupFilter({
            selectedFilterId: selectedRecordGroupFilterId,
            allFilterId: ALL_RECORD_GROUPS_FILTER_ID,
            storedGroupId: null,
            note,
            layout: memberGradeLayout,
          })
        ) {
          continue;
        }
        const timetableHint = (row.event_name ?? '').trim() || '—';
        out.push({
          key: `${termNumber}:fixed-no-source:${row.id}`,
          termNumber,
          hasProgramSchedule: false,
          sortKey: `${termNumber}|\uffff|fixed|${String(row.order_index).padStart(5, '0')}|${row.id}`,
          dateLabel: '—',
          weekdayLabel: '—',
          periodLabel: `고정 ${row.order_index + 1}`,
          programLabel: '행사',
          programDotKind: 'fixed',
          timetableText: timetableHint,
          unitLabel: formatUnitTextForDisplay(row.unit_text ?? ''),
          learningLabel: formatLearningTextForDisplay(row.learning_text ?? ''),
          recordKind: text,
          kindLabel: classRecordKindLabel(text),
          note,
          recordMemberGroupId: null,
          ...(linkedAssignmentItemId ? { linkedAssignmentItemId } : {}),
          ...(linkedAssignmentGroupId ? { linkedAssignmentGroupId } : {}),
        });
      }
    };

    const appendScopedRowsWithoutSourceKey = (rows: readonly ProgramScopedContentRow[], termNumber: 1 | 2) => {
      for (const row of rows) {
        if ((row.source_annual_cell_key ?? '').trim()) continue;
        const raw = row.record_class_text ?? '';
        const { text, note, linkedAssignmentItemId, linkedAssignmentGroupId } =
        resolveClassRecordDisplay(raw);
        if (!note.trim()) continue;
        if (text !== 'lesson' && text !== 'eval_log') continue;
        const storedGid = (row.record_member_group_id ?? '').trim() || null;
        if (
          !recordMatchesClassRecordGroupFilter({
            selectedFilterId: selectedRecordGroupFilterId,
            allFilterId: ALL_RECORD_GROUPS_FILTER_ID,
            storedGroupId: storedGid,
            note,
            layout: memberGradeLayout,
          })
        ) {
          continue;
        }
        out.push({
          key: `${termNumber}:scoped-no-source:${row.id}`,
          termNumber,
          hasProgramSchedule: false,
          sortKey: `${termNumber}|\uffff|scoped|${String(row.order_index).padStart(5, '0')}|${row.id}`,
          dateLabel: '—',
          weekdayLabel: '—',
          periodLabel: `편제 ${row.order_index + 1}`,
          programLabel: programLabelForScopedOrphanRow(row, annualProjectPrograms),
          programDotKind: programDotKindForScopedOrphanRow(row),
          timetableText: timetableLabelForScopedMemosRow(row),
          unitLabel: formatUnitTextForDisplay(row.unit_text ?? ''),
          learningLabel: formatLearningTextForDisplay(row.learning_text ?? ''),
          recordKind: text,
          kindLabel: classRecordKindLabel(text),
          note,
          recordMemberGroupId: storedGid,
          ...(linkedAssignmentItemId ? { linkedAssignmentItemId } : {}),
          ...(linkedAssignmentGroupId ? { linkedAssignmentGroupId } : {}),
        });
      }
    };

    appendFixedRowsWithoutSourceKey(fixedScopedRowsForMemos.f1, 1);
    appendFixedRowsWithoutSourceKey(fixedScopedRowsForMemos.f2, 2);
    appendScopedRowsWithoutSourceKey(projectedScopedRowsForMemos.s1, 1);
    appendScopedRowsWithoutSourceKey(projectedScopedRowsForMemos.s2, 2);

    const membersById = new Map(
      members.map((m) => [m.id, { id: m.id, name: displayClassMemberName(m).trim() }] as const)
    );
    const notesWithGroup = pointMemberNotes
      .map((n) => {
        const groupId = memberGroupById.get(n.memberId) ?? '';
        return groupId ? { ...n, groupId } : null;
      })
      .filter((n): n is MemberNoteRecord & { groupId: string } => !!n);

    out.push(
      ...buildPointNoteMemoRows({
        notes: notesWithGroup,
        membersById,
        workspaceId,
        gradeId: effectiveGradeId,
        grades,
        academicYear,
        selectedEntry,
        rowsTerm1,
        rowsTerm2,
        selectedRecordGroupFilterId,
        allRecordGroupsFilterId: ALL_RECORD_GROUPS_FILTER_ID,
      })
    );

    out.push(
      ...buildBehaviorNoteMemoRows({
        notes: [...behaviorNotesT1, ...behaviorNotesT2],
        selectedEntry,
        rowsTerm1,
        rowsTerm2,
        selectedRecordGroupFilterId,
        allRecordGroupsFilterId: ALL_RECORD_GROUPS_FILTER_ID,
      })
    );

    out.push(
      ...buildAssignmentMemoRows({
        doc: assignmentPointsDoc,
        layout: memberGradeLayout,
        membersById: assignmentMembersById,
        workspaceId,
        academicYear,
        selectedRecordGroupFilterId,
        allRecordGroupsFilterId: ALL_RECORD_GROUPS_FILTER_ID,
      })
    );

    return out;
  }, [
    academicYear,
    annualProjectPrograms,
    assignmentPointsDoc,
    behaviorNotesT1,
    behaviorNotesT2,
    cellOverlays1,
    cellOverlays2,
    cellsT1.cellsByAnnualKey,
    cellsT2.cellsByAnnualKey,
    classParsed?.title,
    effectiveGradeId,
    fixedScopedRowsForMemos,
    projectedScopedRowsForMemos,
    grades,
    members,
    pointMemberNotes,
    rowsTerm1,
    rowsTerm2,
    selectedEntry,
    memberGradeLayout,
    selectedRecordGroupFilterId,
    workspaceId,
  ]);

  const filteredMemoRows = React.useMemo((): MemoRow[] => {
    const rows = memoRowsBase;
    if (selectedGeneralQueryTag != null) {
      if (selectedGeneralQueryTag === UNTAGGED_MEMBER_FILTER_ID) {
        return rows.filter((r) => {
          if (r.sourceMemberId) return true;
          return parseClassRecordMemoTags(r.note).length === 0;
        });
      }
      return rows.filter((r) => {
        if (r.sourceMemberId) {
          const member = members.find((m) => m.id === r.sourceMemberId);
          const memberName = (member?.name ?? '').trim();
          const shownName = member ? displayClassMemberName(member).trim() : '';
          return memberName === selectedGeneralQueryTag || shownName === selectedGeneralQueryTag;
        }
        return parseClassRecordMemoTags(r.note).some(
          (t) =>
            t.kind === 'include' &&
            !t.memberId &&
            t.keyword === selectedGeneralQueryTag
        );
      });
    }
    if (isAllMembersSelected) return rows;
    if (isUntaggedMemberSelected) {
      return rows.filter((r) => !r.sourceMemberId && parseClassRecordMemoTags(r.note).length === 0);
    }
    if (!selectedMember) return [];
    return rows
      .map((r) => {
        if (r.sourceMemberId) {
          if (r.sourceMemberId !== selectedMember.id) return null;
          return r;
        }
        const tagMembers = membersForRecordGroup(memberGradeLayout, r.recordMemberGroupId, members);
        const tagOpts: ClassRecordMemoTagRuleOptions = { applyBulkMemberTagRules: !!r.recordMemberGroupId };
        if (!memoMatchesMember(r.note, selectedMember, tagMembers, tagOpts)) return null;
        const extracted = extractMemberMatchedTaggedLines(r.note, selectedMember, tagMembers, tagOpts);
        if (!extracted.trim()) return null;
        return { ...r, note: extracted };
      })
      .filter((r): r is MemoRow => !!r);
  }, [
    isAllMembersSelected,
    isUntaggedMemberSelected,
    memberGradeLayout,
    members,
    memoRowsBase,
    selectedGeneralQueryTag,
    selectedMember,
  ]);

  const [kindFilter, setKindFilter] = React.useState<Set<string> | null>(null);
  const [programFilter, setProgramFilter] = React.useState<Set<string> | null>(null);
  const [openHeaderPanel, setOpenHeaderPanel] = React.useState<'kind' | 'program' | null>(null);
  const [colWidthsPx, setColWidthsPx] = React.useState<Record<CrmColKey, number>>(loadStoredCrmColWidthsPx);
  const [resizingColKey, setResizingColKey] = React.useState<CrmColKey | null>(null);
  const [panelRect, setPanelRect] = React.useState<{ top: number; left: number; width: number } | null>(
    null
  );
  const kindThBtnRef = React.useRef<HTMLButtonElement>(null);
  const programThBtnRef = React.useRef<HTMLButtonElement>(null);
  const filterPanelRef = React.useRef<HTMLDivElement>(null);
  const classRecordMemosTableWrapRef = React.useRef<HTMLDivElement>(null);
  const classRecordMemosTableRef = React.useRef<HTMLTableElement>(null);
  const classRecordMemosExportRootRef = React.useRef<HTMLDivElement>(null);
  const [classRecordMemosTableFillerRows, setClassRecordMemosTableFillerRows] = React.useState(0);
  const [classRecordMemosExportBusy, setClassRecordMemosExportBusy] = React.useState(false);

  React.useEffect(() => {
    try {
      localStorage.setItem(CRM_COL_WIDTHS_STORAGE_KEY, JSON.stringify(colWidthsPx));
    } catch {
      /* ignore */
    }
  }, [colWidthsPx]);

  const beginCrmColResize = React.useCallback(
    (key: CrmColKey) => (e: React.PointerEvent<HTMLDivElement>) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      e.preventDefault();
      e.stopPropagation();
      const el = e.currentTarget;
      try {
        el.setPointerCapture(e.pointerId);
      } catch {
        /* noop */
      }
      const startX = e.clientX;
      const startW = colWidthsPx[key];
      setResizingColKey(key);

      const onMove = (ev: PointerEvent) => {
        ev.preventDefault();
        const next =
          clampCrmColWidthPx(key, startW + ev.clientX - startX) ?? CRM_DEFAULT_COL_WIDTHS_PX[key];
        setColWidthsPx((prev) => (prev[key] === next ? prev : { ...prev, [key]: next }));
      };
      const onUp = (ev: PointerEvent) => {
        document.removeEventListener('pointermove', onMove);
        document.removeEventListener('pointerup', onUp);
        setResizingColKey(null);
        try {
          el.releasePointerCapture(ev.pointerId);
        } catch {
          /* noop */
        }
      };
      document.addEventListener('pointermove', onMove, { passive: false });
      document.addEventListener('pointerup', onUp);
    },
    [colWidthsPx]
  );

  const renderCrmColResizeHandle = (key: CrmColKey) => (
    <div
      className={
        'cp-class-record-memos-th-resize-handle' + (resizingColKey === key ? ' is-active' : '')
      }
      role="separator"
      aria-orientation="vertical"
      aria-label={`${CRM_COL_LABELS[key]} 열 너비 조절`}
      onPointerDown={beginCrmColResize(key)}
    />
  );

  const syncFilterPanelRect = React.useCallback(() => {
    if (!openHeaderPanel) return;
    const btn =
      openHeaderPanel === 'kind' ? kindThBtnRef.current : programThBtnRef.current;
    const panelEl = filterPanelRef.current;
    if (!btn || !panelEl) return;
    const topEl = panelEl.querySelector('.cp-class-record-memos-filter-panel-top') as HTMLElement | null;
    const innerW = topEl ? Math.ceil(topEl.getBoundingClientRect().width) : 0;
    const padX = 24;
    const w = Math.max(innerW + padX, 100);
    const r = btn.getBoundingClientRect();
    setPanelRect({
      top: r.bottom + 4,
      left: Math.max(8, Math.min(r.left, window.innerWidth - w - 8)),
      width: w,
    });
  }, [openHeaderPanel]);

  React.useLayoutEffect(() => {
    if (!openHeaderPanel) {
      setPanelRect(null);
      return;
    }
    syncFilterPanelRect();
  }, [openHeaderPanel, syncFilterPanelRect]);

  /** 표 헤더 필터(구분·프로그램): 멤버/조회 방식 바뀔 때만 초기화. 기록 조회 그룹은 유지 */
  React.useEffect(() => {
    setKindFilter(null);
    setProgramFilter(null);
    setOpenHeaderPanel(null);
  }, [selectedMemberId, isAllMembersSelected]);

  /** 학년도·클래스가 바뀌면 기록 그룹을 전체로 되돌리고 표 필터도 초기화 */
  React.useEffect(() => {
    setKindFilter(null);
    setProgramFilter(null);
    setOpenHeaderPanel(null);
    setSelectedRecordGroupFilterId(ALL_RECORD_GROUPS_FILTER_ID);
    setSelectedGeneralQueryTag(null);
  }, [academicYear, effectiveGradeId]);

  const sortedRecordGroups = React.useMemo(
    () =>
      [...memberGradeLayout.groups].sort(
        (a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name, 'ko') || a.id.localeCompare(b.id)
      ),
    [memberGradeLayout]
  );

  const selectedRecordGroupName = React.useMemo(() => {
    if (selectedRecordGroupFilterId === ALL_RECORD_GROUPS_FILTER_ID) return '';
    const g = sortedRecordGroups.find((x) => x.id === selectedRecordGroupFilterId);
    return (g?.name ?? '').trim() || '이 그룹';
  }, [selectedRecordGroupFilterId, sortedRecordGroups]);

  /** 전체(그룹)=학급 전체 멤버 통합, 특정 그룹=그 그룹 멤버만 대상으로 한 통합 보기 */
  const aggregateMemberButtonLabel =
    selectedRecordGroupFilterId === ALL_RECORD_GROUPS_FILTER_ID
      ? '모든 멤버 통합'
      : `${selectedRecordGroupName} · 멤버 전체`;

  const mobileMemberFilterValue = React.useMemo(() => {
    if (isAllMembersSelected) return ALL_MEMBERS_FILTER_ID;
    if (selectedGeneralQueryTag != null && selectedGeneralQueryTag !== UNTAGGED_MEMBER_FILTER_ID) {
      return `${GENERAL_TAG_OPTION_PREFIX}${selectedGeneralQueryTag}`;
    }
    if (isUntaggedMemberSelected) return UNTAGGED_MEMBER_FILTER_ID;
    return selectedMemberId ? `${MOBILE_MEMBER_OPTION_PREFIX}${selectedMemberId}` : ALL_MEMBERS_FILTER_ID;
  }, [isAllMembersSelected, isUntaggedMemberSelected, selectedGeneralQueryTag, selectedMemberId]);

  const handleMobileMemberFilterChange = React.useCallback((value: string) => {
    if (value === ALL_MEMBERS_FILTER_ID) {
      setSelectedGeneralQueryTag(null);
      setSelectedMemberId(ALL_MEMBERS_FILTER_ID);
      return;
    }
    if (value === UNTAGGED_MEMBER_FILTER_ID) {
      setSelectedGeneralQueryTag(UNTAGGED_MEMBER_FILTER_ID);
      setSelectedMemberId(UNTAGGED_MEMBER_FILTER_ID);
      return;
    }
    if (!value.startsWith(MOBILE_MEMBER_OPTION_PREFIX)) {
      if (value.startsWith(GENERAL_TAG_OPTION_PREFIX)) {
        const tag = value.slice(GENERAL_TAG_OPTION_PREFIX.length).trim();
        setSelectedGeneralQueryTag(tag || null);
        setSelectedMemberId(ALL_MEMBERS_FILTER_ID);
        return;
      }
      setSelectedGeneralQueryTag(null);
      setSelectedMemberId(ALL_MEMBERS_FILTER_ID);
      return;
    }
    const memberId = value.slice(MOBILE_MEMBER_OPTION_PREFIX.length).trim();
    setSelectedGeneralQueryTag(null);
    setSelectedMemberId(memberId || ALL_MEMBERS_FILTER_ID);
  }, []);

  /**
   * 멤버별 조회: 레이아웃 기준으로 그룹 섹션을 나눈다.
   * - 기록 조회 그룹을 하나 골랐을 때: 한 섹션이며 그룹명은 멤버 버튼 위 제목(h4)으로만 표시(전체 클래스의 다그룹 보기와 동일).
   * - 전체(클래스)이고 멤버 그룹이 둘 이상이면 그룹별 제목 + 멤버 목록.
   */
  /** 멤버 고정 번호(그룹별, 공통 헬퍼) */
  const memberNumberById = React.useMemo(
    () => buildLayoutMemberNumberMap(memberGradeLayout),
    [memberGradeLayout]
  );

  const memberSidebarPlan = React.useMemo((): {
    sections: MemberSidebarSection[];
    showSectionHeadings: boolean;
    flatMembers: ClassMemberRow[];
  } => {
    if (selectedRecordGroupFilterId !== ALL_RECORD_GROUPS_FILTER_ID) {
      const list = memberGradeLayout.membersByGroupId[selectedRecordGroupFilterId] ?? [];
      const g = sortedRecordGroups.find((x) => x.id === selectedRecordGroupFilterId);
      const groupName = (g?.name ?? '').trim() || '멤버 그룹';
      return {
        sections: [{ groupId: selectedRecordGroupFilterId, groupName, members: list }],
        showSectionHeadings: true,
        flatMembers: list,
      };
    }
    if (sortedRecordGroups.length === 0) {
      return {
        sections: [{ groupId: '', groupName: '', members }],
        showSectionHeadings: false,
        flatMembers: members,
      };
    }
    const sections: MemberSidebarSection[] = sortedRecordGroups.map((g) => ({
      groupId: g.id,
      groupName: (g.name ?? '').trim() || '그룹',
      members: memberGradeLayout.membersByGroupId[g.id] ?? [],
    }));
    const nonEmpty = sections.filter((s) => s.members.length > 0);
    if (nonEmpty.length === 0) {
      return {
        sections: [{ groupId: '', groupName: '', members }],
        showSectionHeadings: false,
        flatMembers: members,
      };
    }
    const inLayout = new Set(nonEmpty.flatMap((s) => s.members.map((m) => m.id)));
    const leftovers = members.filter((m) => !inLayout.has(m.id));
    const merged: MemberSidebarSection[] = [...nonEmpty];
    if (leftovers.length) {
      merged.push({ groupId: '__unplaced__', groupName: '기타', members: leftovers });
    }
    const flatMembers = merged.flatMap((s) => s.members);
    return {
      sections: merged,
      showSectionHeadings: merged.length > 1,
      flatMembers,
    };
  }, [members, memberGradeLayout, selectedRecordGroupFilterId, sortedRecordGroups]);

  const sidebarMembersForRecordGroup = memberSidebarPlan.flatMembers;

  /** 이미 로드된 memoRowsBase만 사용 — 추가 네트워크 없음. 현재 카테고리 기준. */
  const memberRecordCountById = React.useMemo(() => {
    const counts = new Map<string, number>();
    for (const m of sidebarMembersForRecordGroup) counts.set(m.id, 0);
    if (sidebarMembersForRecordGroup.length === 0 || memoRowsBase.length === 0) return counts;

    for (const row of memoRowsBase) {
      if (!rowMatchesMemosCategory(row, memosCategory)) continue;

      if (row.sourceMemberId) {
        if (!counts.has(row.sourceMemberId)) continue;
        counts.set(row.sourceMemberId, (counts.get(row.sourceMemberId) ?? 0) + 1);
        continue;
      }

      const tagMembers = membersForRecordGroup(memberGradeLayout, row.recordMemberGroupId, members);
      const tagOpts: ClassRecordMemoTagRuleOptions = {
        applyBulkMemberTagRules: !!row.recordMemberGroupId,
      };
      for (const m of sidebarMembersForRecordGroup) {
        if (!memoMatchesMember(row.note, m, tagMembers, tagOpts)) continue;
        const extracted = extractMemberMatchedTaggedLines(row.note, m, tagMembers, tagOpts);
        if (!extracted.trim()) continue;
        counts.set(m.id, (counts.get(m.id) ?? 0) + 1);
      }
    }

    return counts;
  }, [
    memoRowsBase,
    memosCategory,
    memberGradeLayout,
    members,
    sidebarMembersForRecordGroup,
  ]);

  const sealedLabelByToken = useSealedMemoTagLabels(
    React.useMemo(() => memoRowsBase.map((r) => r.note), [memoRowsBase])
  );

  const generalQueryTagOptions = React.useMemo(() => {
    const memberPool = sidebarMembersForRecordGroup;
    const out = new Set<string>();
    for (const row of memoRowsBase) {
      const tags = parseClassRecordMemoTags(row.note);
      for (const t of tags) {
        if (t.kind !== 'include') continue;
        // 등록 멤버 태그(#m:id)는 멤버별 조회 전용 — 커스텀 태그 목록에 넣지 않음
        if (t.memberId) continue;
        const kw = (t.keyword ?? '').trim();
        if (!kw) continue;
        if (t.sealed || isSealedTagBody(kw)) {
          out.add(kw);
          continue;
        }
        // 레거시 #이름 이 멤버와 유일 매칭되면 멤버 태그로 취급
        if (resolveMemberTagUnique(memberPool, kw)) continue;
        out.add(kw);
      }
    }
    return Array.from(out).sort((a, b) =>
      labelForGeneralQueryTag(a, sealedLabelByToken).localeCompare(
        labelForGeneralQueryTag(b, sealedLabelByToken),
        'ko'
      )
    );
  }, [memoRowsBase, sealedLabelByToken, sidebarMembersForRecordGroup]);

  React.useEffect(() => {
    if (selectedMemberId === ALL_MEMBERS_FILTER_ID) return;
    if (selectedMemberId === UNTAGGED_MEMBER_FILTER_ID) return;
    if (!sidebarMembersForRecordGroup.some((m) => m.id === selectedMemberId)) {
      setSelectedMemberId(ALL_MEMBERS_FILTER_ID);
    }
  }, [selectedMemberId, sidebarMembersForRecordGroup, selectedRecordGroupFilterId]);

  React.useEffect(() => {
    if (selectedGeneralQueryTag == null) return;
    if (selectedGeneralQueryTag === UNTAGGED_MEMBER_FILTER_ID) return;
    if (!generalQueryTagOptions.includes(selectedGeneralQueryTag)) {
      setSelectedGeneralQueryTag(null);
    }
  }, [generalQueryTagOptions, selectedGeneralQueryTag]);

  React.useEffect(() => {
    if (!openHeaderPanel) return;
    const fn = () => syncFilterPanelRect();
    window.addEventListener('scroll', fn, true);
    window.addEventListener('resize', fn);
    return () => {
      window.removeEventListener('scroll', fn, true);
      window.removeEventListener('resize', fn);
    };
  }, [openHeaderPanel, syncFilterPanelRect]);

  React.useEffect(() => {
    if (!openHeaderPanel) return;
    const onDoc = (e: MouseEvent) => {
      const t = e.target as Node;
      if (filterPanelRef.current?.contains(t)) return;
      if (kindThBtnRef.current?.contains(t)) return;
      if (programThBtnRef.current?.contains(t)) return;
      setOpenHeaderPanel(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpenHeaderPanel(null);
    };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [openHeaderPanel]);

  const toggleKindFilterKey = React.useCallback((key: string) => {
    const all = new Set(ALL_KIND_FILTER_KEYS);
    setKindFilter((prev) => {
      if (prev === null) {
        const next = new Set(all);
        next.delete(key);
        return next.size === all.size ? null : next;
      }
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === 0) return new Set();
      if (next.size === all.size && Array.from(all).every((x) => next.has(x))) return null;
      return next;
    });
  }, []);

  const toggleProgramFilterKey = React.useCallback((key: MemoRow['programDotKind']) => {
    const all = new Set<string>(ALL_PROGRAM_FILTER_KEYS);
    setProgramFilter((prev) => {
      if (prev === null) {
        const next = new Set(all);
        next.delete(key);
        return next.size === all.size ? null : next;
      }
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      if (next.size === 0) return new Set();
      if (next.size === all.size && Array.from(all).every((x) => next.has(x))) return null;
      return next;
    });
  }, []);

  const displayMemoRows = React.useMemo(() => {
    let rows = [...filteredMemoRows];
    rows = rows.filter((r) => rowMatchesMemosCategory(r, memosCategory));

    const assignmentMemoOnly =
      kindFilter !== null &&
      kindFilter.size === 1 &&
      kindFilter.has(ASSIGNMENT_MEMO_RECORD_KIND);
    if (!assignmentMemoOnly) {
      const linkedIds = new Set(
        rows
          .filter((r) => r.recordKind === 'lesson' || r.recordKind === 'eval_log')
          .map((r) => (r.linkedAssignmentItemId ?? '').trim())
          .filter(Boolean)
      );
      if (linkedIds.size > 0) {
        rows = rows.filter(
          (r) =>
            r.recordKind !== ASSIGNMENT_MEMO_RECORD_KIND ||
            !(r.assignmentItemId ?? '').trim() ||
            !linkedIds.has((r.assignmentItemId ?? '').trim())
        );
      }
    }

    if (kindFilter !== null) {
      rows = rows.filter((r) => kindFilter.has(kindFilterBucketForRow(r)));
    }
    if (programFilter !== null) {
      rows = rows.filter((r) => programFilter.has(r.programDotKind));
    }
    rows.sort((a, b) => (a.sortKey < b.sortKey ? -1 : a.sortKey > b.sortKey ? 1 : 0));
    return rows;
  }, [filteredMemoRows, kindFilter, memosCategory, programFilter]);

  const exportQueryLabel = React.useMemo(() => {
    if (selectedGeneralQueryTag === UNTAGGED_MEMBER_FILTER_ID) return '태그 없음';
    if (selectedGeneralQueryTag != null) {
      return `#${labelForGeneralQueryTag(selectedGeneralQueryTag, sealedLabelByToken)}`;
    }
    if (isAllMembersSelected) return aggregateMemberButtonLabel;
    if (isUntaggedMemberSelected) return '이름 태그 없음';
    if (selectedMember) return memberDisplayName(selectedMember);
    return '';
  }, [
    aggregateMemberButtonLabel,
    isAllMembersSelected,
    isUntaggedMemberSelected,
    sealedLabelByToken,
    selectedGeneralQueryTag,
    selectedMember,
  ]);

  const exportRecordGroupLabel = React.useMemo(() => {
    if (selectedRecordGroupFilterId === ALL_RECORD_GROUPS_FILTER_ID) return '전체 그룹 및 태그';
    return selectedRecordGroupName;
  }, [selectedRecordGroupFilterId, selectedRecordGroupName]);

  const exportDocumentTitle = React.useMemo(() => {
    const classTitle = (classParsed?.title ?? '').trim() || '클래스';
    return `${academicYear}학년도 · ${classTitle} · 기록 모아보기`;
  }, [academicYear, classParsed?.title]);

  const exportDocumentSubtitle = React.useMemo(
    () => `기록 조회 그룹: ${exportRecordGroupLabel} · 조회: ${exportQueryLabel}`,
    [exportQueryLabel, exportRecordGroupLabel]
  );

  const exportMemoRows = React.useMemo((): ClassRecordMemosExportRow[] => {
    return displayMemoRows.map((r) => ({
      termLabel: r.hasProgramSchedule ? String(r.termNumber) : '',
      dateLabel: r.hasProgramSchedule ? r.dateLabel : '',
      weekdayLabel: r.hasProgramSchedule ? r.weekdayLabel : '',
      periodLabel: r.periodLabel,
      programLabel: r.programLabel,
      unitLabel: r.unitLabel,
      learningLabel: r.learningLabel,
      kindLabel: r.kindLabel,
      note: r.note,
    }));
  }, [displayMemoRows]);

  const recalcClassRecordMemosTableFiller = React.useCallback(() => {
    const wrap = classRecordMemosTableWrapRef.current;
    const table = classRecordMemosTableRef.current;
    if (!wrap || !table) return;
    const n = countClassRecordMemosTableFillerRows(wrap, table, {
      dataRowCount: displayMemoRows.length,
      emptyMessageRow: displayMemoRows.length === 0,
    });
    setClassRecordMemosTableFillerRows((prev) => (prev === n ? prev : n));
  }, [displayMemoRows.length]);

  React.useLayoutEffect(() => {
    const wrap = classRecordMemosTableWrapRef.current;
    if (!wrap) return;
    const ro = new ResizeObserver(() => {
      requestAnimationFrame(recalcClassRecordMemosTableFiller);
    });
    ro.observe(wrap);
    requestAnimationFrame(recalcClassRecordMemosTableFiller);
    return () => ro.disconnect();
  }, [recalcClassRecordMemosTableFiller]);

  const classRecordMemosFillerRowElements = React.useMemo(() => {
    if (classRecordMemosTableFillerRows <= 0) return null;
    return Array.from({ length: classRecordMemosTableFillerRows }, (_, i) => (
      <tr key={`cp-crm-filler-${i}`} className="cp-class-record-memos-tr-filler" aria-hidden>
        {Array.from({ length: 9 }, (_, j) => (
          <td key={j} className="cp-class-record-memos-td-filler">
            {'\u00a0'}
          </td>
        ))}
      </tr>
    ));
  }, [classRecordMemosTableFillerRows]);

  const kindFilterActive = kindFilter !== null && kindFilter.size < ALL_KIND_FILTER_KEYS.length;
  const programFilterActive =
    programFilter !== null && programFilter.size < ALL_PROGRAM_FILTER_KEYS.length;

  React.useLayoutEffect(() => {
    requestAnimationFrame(recalcClassRecordMemosTableFiller);
  }, [recalcClassRecordMemosTableFiller, openHeaderPanel, kindFilterActive, programFilterActive]);

  const openKindPanel = React.useCallback(() => {
    setOpenHeaderPanel((p) => (p === 'kind' ? null : 'kind'));
  }, []);

  const openProgramPanel = React.useCallback(() => {
    setOpenHeaderPanel((p) => (p === 'program' ? null : 'program'));
  }, []);

  const filterPanelAnchor = (() => {
    if (!openHeaderPanel) return null;
    const btn =
      openHeaderPanel === 'kind' ? kindThBtnRef.current : programThBtnRef.current;
    if (!btn) return null;
    const r = btn.getBoundingClientRect();
    return { top: r.bottom + 4, left: r.left };
  })();

  const loading = prog1.loading || prog2.loading || cellsT1.loading || cellsT2.loading || overlayLoading;
  const error = prog1.error || prog2.error || cellsT1.error || cellsT2.error || overlayError;

  const canExportClassRecordMemos =
    !loading &&
    !error &&
    !!selectedEntry &&
    displayMemoRows.length > 0 &&
    (isAllMembersSelected ||
      isUntaggedMemberSelected ||
      selectedGeneralQueryTag != null ||
      !!selectedMember);

  const buildClassRecordMemosExportFileBase = React.useCallback(() => {
    const dateLabel = new Date().toLocaleDateString('ko-KR').replace(/\./g, '').trim();
    const classPart = sanitizeClassRecordMemosExportFileToken(classParsed?.title ?? '클래스');
    const queryPart = sanitizeClassRecordMemosExportFileToken(exportQueryLabel || '조회');
    return `기록모아보기_${academicYear}학년도_${classPart}_${queryPart}_${dateLabel}`;
  }, [academicYear, classParsed?.title, exportQueryLabel]);

  const prepareClassRecordMemosExportClone = React.useCallback((clone: HTMLElement) => {
    clone.style.maxHeight = 'none';
    clone.style.overflow = 'visible';
    clone.style.background = '#ffffff';
    const meta = clone.querySelector('.cp-class-record-memos-export-meta') as HTMLElement | null;
    if (meta) {
      meta.style.visibility = 'visible';
      meta.style.position = 'static';
      meta.style.left = 'auto';
      meta.style.width = '100%';
      meta.style.pointerEvents = 'none';
      meta.style.display = 'flex';
      meta.style.flexDirection = 'column';
      meta.style.alignItems = 'center';
      meta.style.textAlign = 'center';
      meta.querySelectorAll('.cp-class-record-memos-export-title, .cp-class-record-memos-export-subtitle').forEach((node) => {
        if (node instanceof HTMLElement) {
          node.style.width = '100%';
          node.style.textAlign = 'center';
        }
      });
    }
    const tableWrap = clone.querySelector('.cp-class-record-memos-table-wrap') as HTMLElement | null;
    if (tableWrap) {
      tableWrap.style.maxHeight = 'none';
      tableWrap.style.overflow = 'visible';
      tableWrap.style.flex = 'none';
    }
    clone.querySelectorAll('.cp-class-record-memos-tr-filler').forEach((el) => el.remove());
  }, []);

  const handleClassRecordMemosSaveExcel = React.useCallback(async () => {
    if (!canExportClassRecordMemos) return;
    setClassRecordMemosExportBusy(true);
    try {
      await saveClassRecordMemosAsExcel({
        titleLine: exportDocumentTitle,
        subtitleLine: exportDocumentSubtitle,
        rows: exportMemoRows,
        fileName: `${buildClassRecordMemosExportFileBase()}.xlsx`,
      });
    } catch (exportErr) {
      console.error('기록 모아보기 엑셀 저장 실패:', exportErr);
      alert('엑셀 저장에 실패했습니다.');
    } finally {
      setClassRecordMemosExportBusy(false);
    }
  }, [
    buildClassRecordMemosExportFileBase,
    canExportClassRecordMemos,
    exportDocumentSubtitle,
    exportDocumentTitle,
    exportMemoRows,
  ]);

  const handleClassRecordMemosSavePdf = React.useCallback(async () => {
    const root = classRecordMemosExportRootRef.current;
    if (!root || !canExportClassRecordMemos) return;
    setClassRecordMemosExportBusy(true);
    setOpenHeaderPanel(null);
    try {
      await saveModalBlockContentAsPdf({
        root,
        fileName: `${buildClassRecordMemosExportFileBase()}.pdf`,
        bodySelector: '.cp-class-record-memos-export-root',
        captureCss: CLASS_RECORD_MEMOS_CAPTURE_CSS,
        captureMinWidthPx: 1280,
        beforeCapture: () => setOpenHeaderPanel(null),
        prepareBodyClone: prepareClassRecordMemosExportClone,
      });
    } catch (exportErr) {
      console.error('기록 모아보기 PDF 저장 실패:', exportErr);
      alert('PDF 저장에 실패했습니다.');
    } finally {
      setClassRecordMemosExportBusy(false);
    }
  }, [
    buildClassRecordMemosExportFileBase,
    canExportClassRecordMemos,
    prepareClassRecordMemosExportClone,
  ]);

  const handleClassRecordMemosPrint = React.useCallback(async () => {
    const root = classRecordMemosExportRootRef.current;
    if (!root || !canExportClassRecordMemos) return;
    setClassRecordMemosExportBusy(true);
    setOpenHeaderPanel(null);
    try {
      await printModalBlockContentA4({
        root,
        title: exportDocumentTitle,
        bodySelector: '.cp-class-record-memos-export-root',
        captureCss: CLASS_RECORD_MEMOS_CAPTURE_CSS,
        captureMinWidthPx: 1280,
        beforeCapture: () => setOpenHeaderPanel(null),
        prepareBodyClone: prepareClassRecordMemosExportClone,
      });
    } catch (exportErr) {
      console.error('기록 모아보기 프린트 실패:', exportErr);
      alert('프린트에 실패했습니다.');
    } finally {
      setClassRecordMemosExportBusy(false);
    }
  }, [
    canExportClassRecordMemos,
    exportDocumentTitle,
    prepareClassRecordMemosExportClone,
  ]);

  const emptyMemoTitleMessage =
    selectedEntry &&
    !loading &&
    !error &&
    filteredMemoRows.length === 0 &&
    (selectedGeneralQueryTag != null || isAllMembersSelected || isUntaggedMemberSelected || Boolean(selectedMember))
      ? selectedGeneralQueryTag === UNTAGGED_MEMBER_FILTER_ID
        ? '태그가 없는 수업기록 메모가 없습니다.'
        : selectedGeneralQueryTag != null
          ? `#${labelForGeneralQueryTag(selectedGeneralQueryTag, sealedLabelByToken)} 태그가 있는 수업기록 메모가 없습니다.`
          : isAllMembersSelected
            ? '기록이 있는 수업기록 메모가 없습니다.'
            : isUntaggedMemberSelected
              ? '이름 태그가 없는 수업기록 메모가 없습니다.'
              : '이 멤버와 연결된 수업기록 메모가 없습니다.'
      : null;

  return (
    <section className="cp-class-record-memos-page">
      <div className="cp-program-page-header">
        <div className="cp-class-record-memos-page-title-line">
          <h2>기록 모아보기</h2>
          <div className="cp-class-record-memos-category-tabs" role="tablist" aria-label="기록 카테고리">
            {MEMOS_CATEGORY_OPTIONS.map((opt, index) => (
              <React.Fragment key={opt.id}>
                {index === 1 ? (
                  <span className="cp-class-record-memos-category-tabs-divider" aria-hidden />
                ) : null}
                <button
                  type="button"
                  role="tab"
                  aria-selected={memosCategory === opt.id}
                  className={
                    'cp-class-record-memos-category-tab' + (memosCategory === opt.id ? ' is-active' : '')
                  }
                  onClick={() => setMemosCategory(opt.id)}
                >
                  {opt.label}
                </button>
              </React.Fragment>
            ))}
          </div>
          {emptyMemoTitleMessage ? (
            <p className="page-desc cp-class-record-memos-title-empty" role="status">
              {emptyMemoTitleMessage}
            </p>
          ) : null}
        </div>
      </div>

      {!effectiveGradeId ? (
        <ClassRecordMemosEmptyFrame />
      ) : error ? (
        <p className="page-desc" role="alert">
          {error}
        </p>
      ) : loading ? (
        <p className="page-desc">불러오는 중…</p>
      ) : !selectedEntry ? (
        <>
          <ClassRecordMemosEmptyFrame />
        </>
      ) : (
        <>
          <article className="cp-card cp-class-record-memos-controls">
            <div className="cp-class-record-memos-controls-top">
              <div className="cp-weekly-class-hours-controls cp-class-record-memos-controls-toolbar">
                <label>
                  학년도
                  <span
                    className="cp-academic-year-readonly"
                    aria-label={`학년도 ${academicYear}학년도(학사일정 기준 고정)`}
                  >
                    {academicYear}학년도
                  </span>
                </label>
                <div className="cp-class-record-memos-record-group-field">
                  <span className="cp-class-record-memos-record-group-label" id="cp-class-record-memos-record-group-label">
                    기록 조회 그룹
                  </span>
                  <div
                    className="cp-apn-group-tabs cp-class-record-memos-record-group-tabs"
                    role="tablist"
                    aria-labelledby="cp-class-record-memos-record-group-label"
                  >
                    <button
                      type="button"
                      role="tab"
                      aria-selected={selectedRecordGroupFilterId === ALL_RECORD_GROUPS_FILTER_ID}
                      className={`cp-apn-group-tab cp-class-record-memos-record-group-tab--meta${
                        selectedRecordGroupFilterId === ALL_RECORD_GROUPS_FILTER_ID ? ' is-active' : ''
                      }`}
                      onClick={() => {
                        setSelectedGeneralQueryTag(null);
                        setSelectedRecordGroupFilterId(ALL_RECORD_GROUPS_FILTER_ID);
                      }}
                    >
                      전체 그룹 및 태그
                    </button>
                    {sortedRecordGroups.length > 0 ? (
                      <span className="cp-class-record-memos-record-group-tabs-divider" aria-hidden />
                    ) : null}
                    {sortedRecordGroups.map((g) => {
                      const active = selectedRecordGroupFilterId === g.id;
                      const label = (g.name ?? '').trim() || g.id;
                      return (
                        <button
                          key={g.id}
                          type="button"
                          role="tab"
                          aria-selected={active}
                          className={`cp-apn-group-tab${active ? ' is-active' : ''}`}
                          onClick={() => {
                            setSelectedGeneralQueryTag(null);
                            setSelectedRecordGroupFilterId(g.id);
                          }}
                        >
                          {label}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="cp-class-record-memos-controls-actions">
                <ModalDocumentActionButtons
                  onSaveExcel={() => void handleClassRecordMemosSaveExcel()}
                  onSavePdf={() => void handleClassRecordMemosSavePdf()}
                  onPrint={() => void handleClassRecordMemosPrint()}
                  saveDisabled={!canExportClassRecordMemos}
                  saveExcelDisabled={!canExportClassRecordMemos}
                  printDisabled={!canExportClassRecordMemos}
                  busy={classRecordMemosExportBusy}
                />
              </div>
            </div>
          </article>

          <div className="cp-class-record-memos-layout">
            <aside className="cp-class-record-memos-sidebar" aria-label="클래스 멤버">
              <div className="cp-class-record-memos-mobile-member-filter">
                <label>
                  멤버 선택
                  <select
                    value={mobileMemberFilterValue}
                    onChange={(e) => handleMobileMemberFilterChange(e.target.value)}
                    aria-label="멤버별 조회 선택"
                  >
                    <option value={ALL_MEMBERS_FILTER_ID}>{aggregateMemberButtonLabel}</option>
                    <option value={UNTAGGED_MEMBER_FILTER_ID}>이름 태그 없음</option>
                    {generalQueryTagOptions.length > 0 ? (
                      <optgroup label="태그별 조회">
                        {generalQueryTagOptions.map((tag) => (
                          <option key={`mob-general-${tag}`} value={`${GENERAL_TAG_OPTION_PREFIX}${tag}`}>
                            # {labelForGeneralQueryTag(tag, sealedLabelByToken)}
                          </option>
                        ))}
                      </optgroup>
                    ) : null}
                    {memberSidebarPlan.showSectionHeadings
                      ? memberSidebarPlan.sections.map((sec) => (
                          <optgroup key={sec.groupId || 'section'} label={sec.groupName}>
                            {sec.members.map((m, idx) => (
                              <option key={m.id} value={`${MOBILE_MEMBER_OPTION_PREFIX}${m.id}`}>
                                {String(memberNumberById.get(m.id) ?? idx + 1).padStart(2, '0')}번 {memberDisplayName(m)}
                                {m.gender ? ` ${m.gender}` : ''}
                              </option>
                            ))}
                          </optgroup>
                        ))
                      : memberSidebarPlan.sections.flatMap((sec) =>
                          sec.members.map((m, idx) => (
                            <option key={m.id} value={`${MOBILE_MEMBER_OPTION_PREFIX}${m.id}`}>
                              {String(memberNumberById.get(m.id) ?? idx + 1).padStart(2, '0')}번 {memberDisplayName(m)}
                              {m.gender ? ` ${m.gender}` : ''}
                            </option>
                          ))
                        )}
                  </select>
                </label>
              </div>
              <div className="cp-class-record-memos-sidebar-all">
              <h3 className="cp-class-record-memos-sidebar-title">전체조회</h3>
              <ul className="cp-class-record-memos-member-list">
                <li>
                  <button
                    type="button"
                    className={
                      'cp-class-record-memos-member-btn' +
                      (isAllMembersSelected ? ' is-active' : '')
                    }
                    onClick={() => {
                      setSelectedGeneralQueryTag(null);
                      setSelectedMemberId(ALL_MEMBERS_FILTER_ID);
                    }}
                  >
                    {aggregateMemberButtonLabel}
                  </button>
                </li>
              </ul>
              </div>
              <div className="cp-class-record-memos-sidebar-members cp-class-record-memos-sidebar-members--general">
                <h3 className="cp-class-record-memos-sidebar-title cp-class-record-memos-sidebar-title--per-member">
                  태그별 조회
                  <button
                    type="button"
                    className="cp-class-record-memos-general-query-toggle"
                    aria-label={generalQueryExpanded ? '태그별 조회 접기' : '태그별 조회 펼치기'}
                    aria-expanded={generalQueryExpanded}
                    onClick={() => setGeneralQueryExpanded((v) => !v)}
                  >
                    {generalQueryExpanded ? '▼' : '▶'}
                  </button>
                </h3>
                {generalQueryExpanded ? (
                  <div className="cp-class-record-memos-general-query">
                  <ul className="cp-class-record-memos-member-list">
                    <li>
                      <button
                        type="button"
                        className={
                          'cp-class-record-memos-member-btn' +
                          (selectedGeneralQueryTag === UNTAGGED_MEMBER_FILTER_ID ? ' is-active' : '')
                        }
                        onClick={() => {
                          setSelectedGeneralQueryTag(UNTAGGED_MEMBER_FILTER_ID);
                          setSelectedMemberId(UNTAGGED_MEMBER_FILTER_ID);
                        }}
                      >
                        태그 없음
                      </button>
                    </li>
                    {generalQueryTagOptions.map((tag) => (
                      <li key={`general-tag-${tag}`}>
                        <button
                          type="button"
                          className={
                            'cp-class-record-memos-member-btn' +
                            (selectedGeneralQueryTag === tag ? ' is-active' : '')
                          }
                          onClick={() => {
                            setSelectedGeneralQueryTag(tag);
                            setSelectedMemberId(ALL_MEMBERS_FILTER_ID);
                          }}
                        >
                          # {labelForGeneralQueryTag(tag, sealedLabelByToken)}
                        </button>
                      </li>
                    ))}
                    {generalQueryTagOptions.length === 0 ? (
                      <li>
                        <p className="page-desc cp-class-record-memos-sidebar-members-empty">
                          표시할 태그가 없습니다.
                        </p>
                      </li>
                    ) : null}
                  </ul>
                  </div>
                ) : null}
              </div>
              <div className="cp-class-record-memos-sidebar-members">
                <h3 className="cp-class-record-memos-sidebar-title cp-class-record-memos-sidebar-title--per-member">
                  멤버별 조회
                </h3>
              {members.length === 0 ? (
                  <p className="page-desc cp-class-record-memos-sidebar-members-empty">클래스 멤버 메뉴에서 학생을 등록해 주세요.</p>
              ) : sidebarMembersForRecordGroup.length === 0 ? (
                  <p className="page-desc cp-class-record-memos-sidebar-members-empty">
                    {selectedRecordGroupFilterId === ALL_RECORD_GROUPS_FILTER_ID
                      ? '표시할 멤버가 없습니다.'
                      : '이 그룹에 등록된 멤버가 없습니다. 클래스 멤버에서 그룹에 학생을 넣어 주세요.'}
                  </p>
              ) : (
                <div className="cp-class-record-memos-member-sections cp-class-record-memos-member-list--scroll">
                  {memberSidebarPlan.sections.map((sec) => (
                    <section
                      key={sec.groupId || 'flat'}
                      className="cp-class-record-memos-member-section"
                      aria-label={sec.groupName || '멤버 목록'}
                    >
                      {memberSidebarPlan.showSectionHeadings && sec.groupName ? (
                        <h4 className="cp-class-record-memos-member-section-heading">{sec.groupName}</h4>
                      ) : null}
                      <ul className="cp-class-record-memos-member-list">
                        {sec.members.map((m, idx) => (
                          <li key={m.id}>
                            <button
                              type="button"
                              className={
                                'cp-class-record-memos-member-btn' +
                                (selectedMemberId === m.id ? ' is-active' : '')
                              }
                              onClick={() => {
                                setSelectedGeneralQueryTag(null);
                                setSelectedMemberId(m.id);
                              }}
                            >
                              <span className="cp-class-record-memos-member-btn-body">
                                {!memberSidebarPlan.showSectionHeadings && sec.groupName ? (
                                  <span className="cp-class-record-memos-member-btn-group">
                                    {sec.groupName}
                                  </span>
                                ) : null}
                                <span className="cp-class-record-memos-member-btn-main">
                                  <span className="cp-class-record-memos-member-btn-order">
                                    {String(memberNumberById.get(m.id) ?? idx + 1).padStart(2, '0')}번
                                  </span>
                                  <span className="cp-class-record-memos-member-btn-name">
                                    {memberDisplayName(m)}
                                  </span>
                                  {m.gender ? (
                                    <span className="cp-class-record-memos-member-gender"> {m.gender}</span>
                                  ) : null}
                                </span>
                              </span>
                              <span
                                className="cp-class-record-memos-member-btn-count"
                                aria-label={`기록 ${memberRecordCountById.get(m.id) ?? 0}건`}
                              >
                                {memberRecordCountById.get(m.id) ?? 0}
                              </span>
                            </button>
                          </li>
                        ))}
                      </ul>
                    </section>
                  ))}
                </div>
              )}
              </div>
            </aside>

            <div className="cp-class-record-memos-main">
              {!selectedMember &&
              !isAllMembersSelected &&
              !isUntaggedMemberSelected &&
              selectedGeneralQueryTag == null ? (
                <p className="page-desc">멤버를 선택하면 해당 학생과 연결된 메모가 표시됩니다.</p>
              ) : filteredMemoRows.length === 0 ? (
                <>
                  <ClassRecordMemosEmptyTable />
                </>
              ) : (
                <div ref={classRecordMemosExportRootRef} className="cp-class-record-memos-export-shell">
                <div className="cp-class-record-memos-export-root">
                  <div className="cp-class-record-memos-export-meta" aria-hidden="true">
                    <p className="cp-class-record-memos-export-title">{exportDocumentTitle}</p>
                    <p className="cp-class-record-memos-export-subtitle">{exportDocumentSubtitle}</p>
                  </div>
                  <div ref={classRecordMemosTableWrapRef} className="cp-class-record-memos-table-wrap">
                  <table ref={classRecordMemosTableRef} className="cp-class-record-memos-table">
                    <colgroup>
                      {CRM_COL_KEYS.map((key) => (
                        <col
                          key={key}
                          className={`cp-crm-col cp-crm-col--${key}`}
                          style={{
                            width: colWidthsPx[key],
                            minWidth: colWidthsPx[key],
                            maxWidth: colWidthsPx[key],
                          }}
                        />
                      ))}
                    </colgroup>
                    <thead>
                      <tr>
                        <th scope="col">
                          학기
                          {renderCrmColResizeHandle('term')}
                        </th>
                        <th scope="col">
                          날짜
                          {renderCrmColResizeHandle('date')}
                        </th>
                        <th scope="col">
                          요일
                          {renderCrmColResizeHandle('weekday')}
                        </th>
                        <th scope="col">
                          교시
                          {renderCrmColResizeHandle('period')}
                        </th>
                        <th scope="col">
                          <button
                            ref={programThBtnRef}
                            type="button"
                            className={
                              'cp-class-record-memos-th-btn' + (programFilterActive ? ' is-active' : '')
                            }
                            aria-expanded={openHeaderPanel === 'program'}
                            aria-haspopup="dialog"
                            aria-label="프로그램: 필터. 표는 날짜·교시 순으로 정렬됩니다."
                            onClick={openProgramPanel}
                          >
                            <span className="cp-class-record-memos-th-btn-label">프로그램</span>
                            <ClassRecordMemosSortHintIcon />
                          </button>
                          {renderCrmColResizeHandle('program')}
                        </th>
                        <th scope="col">
                          단원
                          {renderCrmColResizeHandle('unit')}
                        </th>
                        <th scope="col">
                          학습내용
                          {renderCrmColResizeHandle('learning')}
                        </th>
                        <th scope="col">
                          <button
                            ref={kindThBtnRef}
                            type="button"
                            className={
                              'cp-class-record-memos-th-btn' + (kindFilterActive ? ' is-active' : '')
                            }
                            aria-expanded={openHeaderPanel === 'kind'}
                            aria-haspopup="dialog"
                            aria-label="구분: 필터. 표는 날짜·교시 순으로 정렬됩니다."
                            onClick={openKindPanel}
                          >
                            <span className="cp-class-record-memos-th-btn-label">구분</span>
                            <ClassRecordMemosSortHintIcon />
                          </button>
                          {renderCrmColResizeHandle('kind')}
                        </th>
                        <th scope="col">
                          메모
                          {renderCrmColResizeHandle('note')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {displayMemoRows.length === 0 ? (
                        <>
                          <tr>
                            <td colSpan={9} className="cp-class-record-memos-empty-filtered">
                              선택한 필터 조건에 맞는 행이 없습니다.
                            </td>
                          </tr>
                          {classRecordMemosFillerRowElements}
                        </>
                      ) : (
                        <>
                          {displayMemoRows.map((r) => (
                            <tr key={r.key}>
                              <td
                                className={
                                  r.hasProgramSchedule ? undefined : 'cp-class-record-memos-td-no-schedule-slot'
                                }
                              >
                                {r.hasProgramSchedule ? r.termNumber : null}
                              </td>
                              <td
                                className={
                                  r.hasProgramSchedule ? undefined : 'cp-class-record-memos-td-no-schedule-slot'
                                }
                              >
                                {r.hasProgramSchedule ? r.dateLabel : null}
                              </td>
                              <td
                                className={
                                  r.hasProgramSchedule ? undefined : 'cp-class-record-memos-td-no-schedule-slot'
                                }
                              >
                                {r.hasProgramSchedule ? r.weekdayLabel : null}
                              </td>
                              <td
                                className={
                                  r.hasProgramSchedule ? undefined : 'cp-class-record-memos-td-no-schedule-slot'
                                }
                              >
                                {r.hasProgramSchedule ? r.periodLabel : null}
                              </td>
                              <td className="cp-class-record-memos-program-cell">
                                {r.programDotKind === 'none' ? (
                                  r.programLabel
                                ) : (
                                  <span className="cp-class-record-memos-program-inner">
                                    <span
                                      className={
                                        'cp-class-record-memos-program-mark cp-class-record-memos-program-mark--' +
                                        r.programDotKind
                                      }
                                      aria-hidden
                                    />
                                    {r.programLabel}
                                  </span>
                                )}
                              </td>
                              <td className="cp-class-record-memos-unit-cell">{r.unitLabel}</td>
                              <td className="cp-class-record-memos-learning-cell">{r.learningLabel}</td>
                              <td className="cp-class-record-memos-kind-cell">
                                <span
                                  className={`cp-program-class-rec-inline cp-program-class-rec-inline-${r.recordKind}`}
                                >
                                  {r.kindLabel}
                                </span>
                              </td>
                              <td className="cp-class-record-memos-note-cell">
                                {renderNoteWithMemberTags(
                                  r.note,
                                  membersForRecordGroup(memberGradeLayout, r.recordMemberGroupId, members),
                                  {
                                    applyBulkMemberTagRules: !!r.recordMemberGroupId,
                                    sealedLabelByToken,
                                  }
                                )}
                              </td>
                            </tr>
                          ))}
                          {classRecordMemosFillerRowElements}
                        </>
                      )}
                    </tbody>
                  </table>
                  </div>
                  {openHeaderPanel
                    ? createPortal(
                        <div
                          ref={filterPanelRef}
                          className="cp-class-record-memos-filter-panel"
                          role="dialog"
                          aria-label={
                            openHeaderPanel === 'kind' ? '구분 필터' : '프로그램 필터'
                          }
                          style={{
                            position: 'fixed',
                            top: panelRect?.top ?? filterPanelAnchor?.top ?? 0,
                            left: panelRect?.left ?? filterPanelAnchor?.left ?? 0,
                            width: panelRect?.width,
                            zIndex: 4000,
                          }}
                        >
                          {openHeaderPanel === 'kind' ? (
                            <>
                              <div className="cp-class-record-memos-filter-panel-top">
                                <div className="cp-class-record-memos-filter-panel-title">구분</div>
                                <div className="cp-class-record-memos-filter-panel-actions">
                                  <button
                                    type="button"
                                    className="cp-class-record-memos-filter-chip"
                                    onClick={() => setKindFilter(null)}
                                  >
                                    전체선택
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-class-record-memos-filter-chip"
                                    onClick={() => setKindFilter(new Set())}
                                  >
                                    선택해제
                                  </button>
                                </div>
                              </div>
                              <ul className="cp-class-record-memos-filter-check-list">
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={
                                        kindFilter === null || kindFilter.has(KIND_FILTER_EMPTY_KEY)
                                      }
                                      onChange={() => toggleKindFilterKey(KIND_FILTER_EMPTY_KEY)}
                                    />
                                    <span>(비어 있음)</span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={kindFilter === null || kindFilter.has('lesson')}
                                      onChange={() => toggleKindFilterKey('lesson')}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-lesson">
                                      수업기록
                                    </span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={kindFilter === null || kindFilter.has('eval_log')}
                                      onChange={() => toggleKindFilterKey('eval_log')}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-eval_log">
                                      평가기록
                                    </span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={kindFilter === null || kindFilter.has('behavior')}
                                      onChange={() => toggleKindFilterKey('behavior')}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-behavior">
                                      행동특성
                                    </span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={kindFilter === null || kindFilter.has('point_general')}
                                      onChange={() => toggleKindFilterKey('point_general')}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-point_general">
                                      일반
                                    </span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={kindFilter === null || kindFilter.has('point_learning')}
                                      onChange={() => toggleKindFilterKey('point_learning')}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-point_learning">
                                      학습
                                    </span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={kindFilter === null || kindFilter.has('point_behavior')}
                                      onChange={() => toggleKindFilterKey('point_behavior')}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-point_behavior">
                                      행동
                                    </span>
                                  </label>
                                </li>
                                <li>
                                  <label className="cp-class-record-memos-filter-check-label">
                                    <input
                                      type="checkbox"
                                      checked={
                                        kindFilter === null || kindFilter.has(ASSIGNMENT_MEMO_RECORD_KIND)
                                      }
                                      onChange={() => toggleKindFilterKey(ASSIGNMENT_MEMO_RECORD_KIND)}
                                    />
                                    <span className="cp-program-class-rec-inline cp-program-class-rec-inline-assignment_memo">
                                      과제메모
                                    </span>
                                  </label>
                                </li>
                              </ul>
                            </>
                          ) : (
                            <>
                              <div className="cp-class-record-memos-filter-panel-top">
                                <div className="cp-class-record-memos-filter-panel-title">프로그램</div>
                                <div className="cp-class-record-memos-filter-panel-actions">
                                  <button
                                    type="button"
                                    className="cp-class-record-memos-filter-chip"
                                    onClick={() => setProgramFilter(null)}
                                  >
                                    전체선택
                                  </button>
                                  <button
                                    type="button"
                                    className="cp-class-record-memos-filter-chip"
                                    onClick={() => setProgramFilter(new Set())}
                                  >
                                    선택해제
                                  </button>
                                </div>
                              </div>
                              <ul className="cp-class-record-memos-filter-check-list">
                                {ALL_PROGRAM_FILTER_KEYS.map((k) => (
                                  <li key={k}>
                                    <label className="cp-class-record-memos-filter-check-label">
                                      <input
                                        type="checkbox"
                                        checked={programFilter === null || programFilter.has(k)}
                                        onChange={() => toggleProgramFilterKey(k)}
                                      />
                                      <span className="cp-class-record-memos-program-filter-row">
                                        <span
                                          className={
                                            'cp-class-record-memos-program-mark cp-class-record-memos-program-mark--' +
                                            k
                                          }
                                          aria-hidden
                                        />
                                        {programFilterLabel(k)}
                                      </span>
                                    </label>
                                  </li>
                                ))}
                              </ul>
                            </>
                          )}
                        </div>,
                        document.body
                      )
                    : null}
                </div>
                </div>
              )}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default ClassRecordMemosPage;
